// ========================================
// Main Application Entry Point
// ========================================

const App = {
    currentView: 'dashboard',
    views: {},

    async init() {
        console.log('Initializing SICAF - Sistema Integral de Control Académico y Formación...');
        // The school's names and contact (Ajustes → Datos del centro), needed even before login.
        if (typeof SchoolService !== 'undefined') { await SchoolService.load(); this.applySchoolBranding(); }

        // After a logout the page loads at /#ecpv-curtain (see AuthManager.redirectToStart):
        // keep the curtain showing and give the address back its clean form.
        if (window.location.hash === '#ecpv-curtain') {
            document.getElementById('ecpv-curtain')?.classList.add('ecpv-curtain-shown');
            history.replaceState(null, '', window.location.pathname);
        }

        // Public pages have clean addresses (/inscribete, /inscribete/<token>,
        // /restablecer-contrasena/<token>). Links from older e-mails still use "#": they are
        // moved to the clean address here, so both keep working.
        const legacy = /^#(inscribete(?:\/[^/?#]*)?|restablecer-contrasena\/[^/?#]*)$/.exec(window.location.hash);
        if (legacy) history.replaceState(null, '', '/' + legacy[1]);
        // Screens inside the app have clean addresses too (/alumnado, /alumno/<nombre>). Old "#"
        // links (bookmarks, a link opened in a new tab) are moved to the clean address here.
        else if (this.routeFromHash(window.location.hash)) history.replaceState(null, '', '/' + this.routeFromHash(window.location.hash));
        const publicPath = window.location.pathname.replace(/^\/+/, '');

        // Capture the reset secret before session checks and erase it from the URL.
        // No token is persisted in browser storage or sent as a query parameter.
        if (publicPath.startsWith('restablecer-contrasena/')) {
            const token = publicPath.slice('restablecer-contrasena/'.length);
            history.replaceState(null, '', '/');
            LoginView.mode = /^[a-f0-9]{64}$/.test(token) ? 'reset' : 'recovery';
            LoginView.resetToken = LoginView.mode === 'reset' ? token : null;
            this.renderLogin();
            return;
        }

        // Verify the server session before hydrating personal data or rendering views.
        if (typeof AuthManager !== 'undefined') {
            await AuthManager.init();
        }

        // Check for public enrollment route first
        if (/^inscribete(\/[^/]*)?$/.test(publicPath)) {
            this.renderPublicEnrollment(publicPath);
            return;
        }

        // Check Login
        if (!AuthManager.isLoggedIn()) {
            this.renderLogin();
            this.showSessionEndedNotice();
            return;
        }
        // Shared classroom computers: a tab or browser that did not sign in itself ends the session.
        if (!AuthManager.tabSessionValid()) {
            await AuthManager.endSession();
            this.renderLogin();
            return;
        }
        AuthManager.startIdleWatch();

        if (typeof FileStorage !== 'undefined') {
            await FileStorage.init();
            // School study plans (database); views read them synchronously afterwards.
            if (typeof FormationTypeService !== 'undefined') await FormationTypeService.load().catch(() => {});
            if (typeof StudyPlanService !== 'undefined') await StudyPlanService.load().catch(() => {});
        }

        // Carga la matriz de Permisos por Rol en segundo plano — no bloquea el primer
        // pintado (mismo patrón que CategoryDashboards.syncWidgetConfigFromServer más
        // abajo). Hasta que llegue, todo se muestra (canView() por defecto permite lo
        // no configurado), y en cuanto resuelve se vuelve a aplicar sobre el sidebar y
        // se re-renderiza la vista activa por si tenía widgets que ocultar.
        if (typeof RolePermissions !== 'undefined') {
            RolePermissions.load().then(() => {
                this._applyNavPermissions();
                const dashViews = ['academico', 'alumnocrm', 'rrhh', 'logistica'];
                if (dashViews.includes(this.currentView)) {
                    this.navigate(this.currentView, false);
                }
            });
        }

        // Si venimos de un login recién completado (ver js/login.js), inyecta ya la
        // cortina de transición cubriendo la pantalla, antes de montar nada más, para
        // que el usuario nunca vea el "flash" del dashboard construyéndose por debajo.
        const showLoginReveal = sessionStorage.getItem('ecpv_login_reveal') === '1';
        if (showLoginReveal) {
            sessionStorage.removeItem('ecpv_login_reveal');
            this._injectLoginCurtain();
        }

        // Consumido una sola vez por DashboardView en su primer render: solo la carga
        // inicial justo tras el login trae el fade/slide escalonado de hero+widgets
        // (ver .entrance-anim en dashboard-redesign-proposal.css). Navegar de vuelta al
        // dashboard durante el uso normal no debe repetir la animación cada vez.
        this.justLoggedIn = showLoginReveal;

        // Initialize modal manager
        if (typeof ModalManager !== 'undefined') {
            ModalManager.init();
        }

        // Initialize AI Assistant (Role restricted if needed)
        if (typeof AIAssistant !== 'undefined') {
            AIAssistant.init();
        }

        // Initialize data migration if needed
        if (typeof DataMigration !== 'undefined') {
            DataMigration.runIfNeeded();
        }

        // Register all views
        this.registerViews();

        // Setup navigation
        this.setupNavigation();

        // Setup global form handlers
        this.setupGlobalFormHandlers();

        // Setup global click listener to auto-close dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.quick-create-container') && !e.target.closest('.topbar-profile')) {
                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                    menu.classList.remove('show');
                });
                document.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', 'false');
            }
        });

        // Show Sidebar (it might be hidden from login)
        document.getElementById('sidebar').style.display = 'flex';
        const appContainer = document.querySelector('.app-container');
        if (appContainer) {
            appContainer.classList.remove('login-mode');
            appContainer.style.display = 'flex';
        }

        // Setup User Info in Sidebar and Header
        const currentUser = AuthManager.getCurrentUser();
        if (currentUser) {
            document.body.setAttribute('data-user-role', currentUser.role || '');
        } else {
            document.body.removeAttribute('data-user-role');
        }

        this.setupUserInfo();
        this.setupHeaderInfo();

        // Anima la entrada del sidebar (delay 0ms) justo cuando se muestra por primera
        // vez tras el login; el resto del dashboard (hero, KPIs, gráficos) trae su propio
        // escalonado vía CSS (.dnr-hero / .dashboard-widget-card). La barra superior NO
        // lleva animación propia: se queda fija/ya renderizada y simplemente se descubre
        // según la cortina se desliza sobre ella (ver .ecpv-curtain-panel).
        if (showLoginReveal) {
            const sidebarEl = document.getElementById('sidebar');
            sidebarEl?.classList.add('ecpv-entrance', 'ecpv-entrance-delay-0');

            // El fill-mode "both" de la animación deja un transform (translateY(0),
            // identidad pero no "none") pegado al sidebar para siempre si no se
            // quitan las clases al terminar. Ese transform crea un containing block
            // nuevo y rompe el position:fixed del flyout de segundo nivel, que
            // entonces queda recortado/invisible al hacer hover — solo se arreglaba
            // recargando la página (sin sessionStorage ya no se añaden estas clases).
            if (sidebarEl) {
                const cleanup = () => sidebarEl.classList.remove('ecpv-entrance', 'ecpv-entrance-delay-0');
                sidebarEl.addEventListener('animationend', cleanup, { once: true });
                setTimeout(cleanup, 700); // red de seguridad si animationend no llega a disparar
            }
        }

        // Load the initial view from the address, or the dashboard
        this.navigate(this.currentRoute()); // Navigate checks permissions too

        // Abre la cortina para revelar el dashboard ya montado por debajo. En una
        // recarga normal la cortina de arranque de index.html se quita sin animación.
        if (showLoginReveal) {
            this._revealLoginCurtain();
        } else {
            document.getElementById('ecpv-curtain')?.remove();
        }

        // Trae de la base de datos la disposición de widgets del usuario (no bloquea
        // la navegación; si el dashboard activo ya se pintó con valores por defecto,
        // se refresca en cuanto llega la respuesta).
        if (typeof CategoryDashboards !== 'undefined') {
            CategoryDashboards.syncWidgetConfigFromServer();
        }

        // Load interviews data to set badge count
        if (AuthManager.isAdmin() || AuthManager.isCoordinator()) {
            if (typeof EntrevistasView !== 'undefined') {
                EntrevistasView.loadData();
            }
        }

        // Check for upcoming hires (RRHH/Admin only)
        if (AuthManager.isRRHH() || AuthManager.isAdmin()) {
            this.checkUpcomingHires();
        }

        // Check for first login and force/suggest password change
        if (AuthManager.currentUser && AuthManager.currentUser.firstLogin) {
            this.showFirstLoginPasswordChangePrompt();
        }

        // Refresco dinámico y moderado en segundo plano (mensajes y notificaciones)
        // Intervalo moderado (10 seg) con detección de visibilidad para no saturar el servidor
        let isSyncing = false;
        const performBackgroundSync = async () => {
            if (!AuthManager.isLoggedIn() || typeof FileStorage === 'undefined' || isSyncing) return;
            // Si la pestaña está oculta o minimizada, pausar para ahorrar peticiones
            if (document.hidden) return;
            
            isSyncing = true;
            try {
                // Only downloads full lists when the server fingerprint changed.
                await FileStorage.syncCommunicationsIfChanged({
                    messages: typeof MessagesView === 'undefined' || !MessagesView.isComposing
                });
                if (typeof CommunityUnread !== 'undefined') await CommunityUnread.refreshIfChanged();
            } finally {
                isSyncing = false;
            }
        };

        setInterval(performBackgroundSync, 10000);
        if (typeof CommunityUnread !== 'undefined') CommunityUnread.refresh();

        // Al volver el usuario a la pestaña activa, comprobar inmediatamente
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) performBackgroundSync();
        });
        window.addEventListener('focus', () => {
            performBackgroundSync();
        });

        // Sincronización instantánea entre pestañas del mismo navegador (0 peticiones)
        window.addEventListener('storage', (e) => {
            if (e.key === 'ecpv_messages') {
                if (typeof FileStorage !== 'undefined') {
                    try {
                        FileStorage.dataStore.messages = JSON.parse(e.newValue || '[]');
                    } catch (_) {}
                }
                this.setupHeaderInfo();
                if (typeof DashboardView !== 'undefined' && DashboardView.updateMessagesWidget) {
                    DashboardView.updateMessagesWidget();
                }
                if (this.currentView === 'mensajes' && typeof MessagesView !== 'undefined' && !MessagesView.isComposing) {
                    MessagesView.refresh(false);
                }
            }
        });

        console.log('Application initialized successfully as ' + AuthManager.currentUser.role);
    },

    // ========================================
    // Transición post-login (cortina de apertura / split-screen reveal)
    // ========================================
    // El dashboard se reconstruye tras un reload completo de la página (login.js
    // hace window.location.reload() al autenticar), así que no podemos animar un
    // simple "cierre" de los paneles del login: en su lugar, la nueva carga de
    // página cubre la pantalla con esta cortina de dos paneles, monta el dashboard
    // por debajo sin que se note el parpadeo, y luego la abre para revelarlo.
    _injectLoginCurtain() {
        // index.html already carries the (hidden) boot curtain: just show it.
        const existing = document.getElementById('ecpv-curtain');
        if (existing) {
            existing.classList.add('ecpv-curtain-shown');
            return;
        }

        const curtain = document.createElement('div');
        curtain.id = 'ecpv-curtain';
        curtain.className = 'ecpv-curtain';
        SafeHTML.set(curtain, `
            <div class="ecpv-curtain-panel ecpv-curtain-left"></div>
            <div class="ecpv-curtain-panel ecpv-curtain-right"></div>
        `);
        document.body.appendChild(curtain);

        // Red de seguridad: si algo impide llegar a _revealLoginCurtain() (p.ej. una
        // excepción en otro punto de init()), no dejamos al usuario con la pantalla
        // bloqueada para siempre.
        setTimeout(() => curtain.remove(), 4000);
    },

    _revealLoginCurtain() {
        const curtain = document.getElementById('ecpv-curtain');
        if (!curtain) return;

        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) {
            curtain.remove();
            return;
        }

        // Doble rAF: garantiza que el navegador pinte la cortina en su posición
        // cerrada antes de añadir la clase que dispara la transición de apertura.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                curtain.classList.add('ecpv-curtain-open');
            });
        });

        curtain.addEventListener('transitionend', () => curtain.remove(), { once: true });
        setTimeout(() => curtain.remove(), 900);
    },

    canUseAdminActions() {
        return AuthManager.getCurrentUser()?.role === 'admin';
    },

    setupAdminActions() {
        const container = document.querySelector('.quick-create-container');
        if (!container) return;
        const allowed = this.canUseAdminActions();
        container.hidden = !allowed;
        container.style.display = allowed ? '' : 'none';
        if (!allowed) {
            document.getElementById('quick-create-dropdown')?.classList.remove('show');
            container.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', 'false');
        }
    },

    runAdminAction(action) {
        if (!this.canUseAdminActions()) { this.setupAdminActions(); return; }
        const actions = {
            lead: {view:'leads', open:() => LeadsView.openAddModal()},
            student: {view:'alumnado', open:() => StudentView.openAddModal()},
            professor: {view:'profesores', open:() => ProfessorsView.openNewProfessorModal()},
            subjects: {view:'plan-estudios'},
            formations: {view:'formaciones'}
        };
        const target = Object.hasOwn(actions, action) && actions[action];
        if (!target) return;
        document.getElementById('quick-create-dropdown')?.classList.remove('show');
        document.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', 'false');
        this.navigate(target.view);
        // Use the module's real form, never a delayed click on an arbitrary button
        // or a parallel form that invents academic defaults and saves locally.
        if (this.canUseAdminActions() && this.currentView === target.view) target.open?.();
    },

    toggleDropdown(id, event) {
        if (event) event.stopPropagation();
        if (id === 'quick-create-dropdown' && !this.canUseAdminActions()) {
            this.setupAdminActions();
            return;
        }
        const targetMenu = document.getElementById(id);
        if (!targetMenu) return;
        const isOpen = targetMenu.classList.contains('show');
        
        // Close all dropdowns first
        document.querySelectorAll('.dropdown-menu').forEach(menu => {
            menu.classList.remove('show');
        });
        document.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', 'false');
        
        if (!isOpen) {
            targetMenu.classList.add('show');
        }
        if (id === 'quick-create-dropdown') document.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', String(!isOpen));
    },

    showFirstLoginPasswordChangePrompt() {
        if (typeof Swal === 'undefined') return;

        SafeHTML.fire({
            title: 'Cambia tu contraseña',
            text: 'Has iniciado sesión con una contraseña temporal. Por motivos de seguridad, te sugerimos que cambies tu contraseña por una de tu elección ahora.',
            icon: 'info',
            html: `
                <div style="text-align: left; margin-top: 1.5rem;">
                    <div style="margin-bottom: 1.25rem;">
                        <label for="swal-current-password" style="display: block; font-size: 0.9rem; font-weight: 600; margin-bottom: 0.25rem; color: var(--text-primary);">Contraseña actual o temporal</label>
                        <input type="password" id="swal-current-password" class="swal2-input" style="width: 100%; margin: 0; box-sizing: border-box;" autocomplete="current-password" required>
                    </div>
                    <div style="margin-bottom: 1.25rem; position: relative;">
                        <label for="swal-new-password" style="display: block; font-size: 0.9rem; font-weight: 600; margin-bottom: 0.25rem; color: var(--text-primary);">Nueva Contraseña</label>
                        <input type="password" id="swal-new-password" class="swal2-input" style="width: 100%; margin: 0; box-sizing: border-box; padding-right: 2.75rem;" autocomplete="new-password" placeholder="Escribe tu nueva contraseña...">
                        <button type="button" ${UIEvents.attr("click", function(event,captured){App.toggleSwalPasswordVisibility('swal-new-password',this);}, [])} style="position: absolute; right: 0.5rem; bottom: 0.6rem; background: none; border: none; cursor: pointer; color: #64748b; font-size: 1.1rem; padding: 0.25rem; outline: none; z-index: 10;">
                            <i class="fas fa-eye"></i>
                        </button>
                    </div>
                    <div style="position: relative;">
                        <label for="swal-confirm-password" style="display: block; font-size: 0.9rem; font-weight: 600; margin-bottom: 0.25rem; color: var(--text-primary);">Confirmar Contraseña</label>
                        <input type="password" id="swal-confirm-password" class="swal2-input" style="width: 100%; margin: 0; box-sizing: border-box; padding-right: 2.75rem;" autocomplete="new-password" placeholder="Repite tu nueva contraseña...">
                        <button type="button" ${UIEvents.attr("click", function(event,captured){App.toggleSwalPasswordVisibility('swal-confirm-password',this);}, [])} style="position: absolute; right: 0.5rem; bottom: 0.6rem; background: none; border: none; cursor: pointer; color: #64748b; font-size: 1.1rem; padding: 0.25rem; outline: none; z-index: 10;">
                            <i class="fas fa-eye"></i>
                        </button>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonColor: '#0F766E',
            confirmButtonText: 'Guardar Contraseña',
            preConfirm: () => {
                const currentPassword = document.getElementById('swal-current-password').value;
                const newPassword = document.getElementById('swal-new-password').value;
                const confirmPassword = document.getElementById('swal-confirm-password').value;

                if (!currentPassword) {
                    Swal.showValidationMessage('Introduce la contraseña actual o temporal con la que has iniciado sesión.');
                    return false;
                }
                if (!newPassword) {
                    Swal.showValidationMessage('¡Debes introducir una contraseña!');
                    return false;
                }
                if (newPassword.length < 10) {
                    Swal.showValidationMessage('La contraseña debe tener al menos 10 caracteres.');
                    return false;
                }
                if (newPassword !== confirmPassword) {
                    Swal.showValidationMessage('Las contraseñas no coinciden. Por favor, verifícalas.');
                    return false;
                }

                return { password: newPassword, currentPassword };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                const { password, currentPassword } = result.value;
                try {
                    const token = AuthManager.getCsrfToken();
                    const userId = AuthManager.currentUser.id;
                    const response = await fetch(`/api/users/${userId}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SICAF-CSRF': token
                        },
                        body: JSON.stringify({ password, currentPassword })
                    });

                    const res = await response.json();
                    if (res.success) {
                        AuthManager.storeSession(res);
                        
                        SafeHTML.fire({
                            icon: 'success',
                            title: 'Contraseña actualizada',
                            text: 'Tu contraseña se ha cambiado correctamente.',
                            timer: 2000,
                            showConfirmButton: false
                        });
                    } else {
                        throw new Error(res.error || 'Error al guardar');
                    }
                } catch (err) {
                    SafeHTML.fire('Error', err.message || 'No se pudo actualizar la contraseña. Reinténtalo.', 'error').then(() => {
                        this.showFirstLoginPasswordChangePrompt();
                    });
                }
            }
        });
    },

    toggleSwalPasswordVisibility(inputId, buttonEl) {
        const input = document.getElementById(inputId);
        const icon = buttonEl.querySelector('i');
        if (input && icon) {
            if (input.type === 'password') {
                input.type = 'text';
                icon.classList.remove('fa-eye');
                icon.classList.add('fa-eye-slash');
            } else {
                input.type = 'password';
                icon.classList.remove('fa-eye-slash');
                icon.classList.add('fa-eye');
            }
        }
    },

    showSessionEndedNotice() {
        if (AuthManager.takeSessionEndedNotice() === 'idle' && typeof Utils !== 'undefined')
            Utils.showToast('Tu sesión se cerró tras 30 minutos sin actividad. Vuelve a entrar.', 'info');
    },

    // Static shell texts that carry the school's name (Ajustes → Datos del centro).
    applySchoolBranding() {
        if (typeof SchoolService === 'undefined') return;
        // Logos from Ajustes → Imagen; without one, the school's short name as text.
        document.querySelectorAll('[data-school-logo]').forEach(img => {
            const url = SchoolService.image(img.dataset.schoolLogo);
            if (url) img.src = url;
            img.hidden = !url;
            document.querySelectorAll(`[data-school-logo-text="${img.dataset.schoolLogo}"]`).forEach(text => { text.hidden = !!url; });
        });
        const icon = SchoolService.image('iconUrl');
        if (icon) {
            const link = document.querySelector('link[data-school-icon]');
            if (link) { link.href = icon; link.removeAttribute('type'); }
            let touch = document.querySelector('link[rel="apple-touch-icon"]');
            if (!touch) { touch = document.createElement('link'); touch.rel = 'apple-touch-icon'; document.head.appendChild(touch); }
            touch.href = icon;
        }
        document.querySelectorAll('[data-school-short]').forEach(el => { el.textContent = SchoolService.shortName(); });
        document.querySelectorAll('[data-school-alt]').forEach(el => { el.alt = SchoolService.label(el.dataset.schoolAlt); });
        document.querySelectorAll('[data-school-label]').forEach(el => {
            const text = SchoolService.label(el.dataset.schoolLabel);
            el.title = text; el.setAttribute('aria-label', text);
        });
    },

    renderLogin() {
        const contentArea = document.getElementById('content-area');
        // Hide sidebar for login
        document.getElementById('sidebar').style.display = 'none';
        document.querySelector('.app-container').classList.add('login-mode');

        if (typeof LoginView !== 'undefined') {
            // The login replaces the whole body: keep the curtain and open it onto the form.
            const curtain = document.getElementById('ecpv-curtain');
            SafeHTML.set(document.body, LoginView.render());
            LoginView.init();
            if (curtain) {
                document.body.appendChild(curtain);
                this._revealLoginCurtain();
            }
        }
    },

    renderPublicEnrollment(hash) {
        document.body.className = 'public-enrollment-mode';
        
        // Render InscripcionView directly in body
        if (typeof InscripcionView !== 'undefined') {
            SafeHTML.set(document.body, InscripcionView.render(hash));
            InscripcionView.init();
        } else {
            SafeHTML.set(document.body, '<div class="container text-center mt-5"><h3>Cargando sistema de inscripción...</h3><p>Por favor espera.</p></div>');
            setTimeout(() => {
                if (typeof InscripcionView !== 'undefined') {
                    SafeHTML.set(document.body, InscripcionView.render(hash));
                    InscripcionView.init();
                } else {
                    SafeHTML.set(document.body, '<div class="container text-center mt-5 text-danger"><h3>Error al cargar formulario</h3><p>No se pudo inicializar la vista de matrícula. Por favor contacta con soporte.</p></div>');
                }
            }, 1000);
        }
    },

    setupUserInfo() {
        // Update Workspace selector to show User Name and Role
        const user = AuthManager.getCurrentUser();
        const workspaceName = document.querySelector('.workspace-name');
        const workspaceIcon = document.querySelector('.workspace-icon');

        if (user && workspaceName) {
            workspaceName.textContent = `${user.name} (${user.role})`;
            workspaceIcon.textContent = user.name.charAt(0).toUpperCase();

            // Add Click Event for Profile Navigation
            const selector = document.querySelector('.workspace-selector');
            if (selector) {
                selector.style.cursor = 'pointer';
                selector.onclick = () => {
                    if (user.professorId) {
                        this.navigate(`profesor/${user.professorId}`);
                    } else {
                        this.navigate('profile');
                    }
                };
            }
        }

        // Avatar del sidebar (Nivel 1 del riel de iconos): mismo destino que el
        // selector de workspace, pero siempre visible en vez de vivir en un menú.
        const sidebarAvatarBtn = document.getElementById('sidebar-user-avatar-btn');
        const sidebarAvatarEl = document.getElementById('sidebar-user-avatar');
        if (user && sidebarAvatarBtn && sidebarAvatarEl) {
            let photoUrl = user.photo;
            if (!photoUrl && user.professorId && typeof DataManager !== 'undefined') {
                const professor = DataManager.getProfessor(user.professorId);
                if (professor && professor.photo) photoUrl = professor.photo;
            }
            if (photoUrl) {
                StudentPhotos.setIdentityHTML(sidebarAvatarEl, `<img ${StudentPhotos.attributes(photoUrl)} alt="${Utils.sanitizeHTML(user.name)}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;">`);
            } else {
                sidebarAvatarEl.textContent = user.name.charAt(0).toUpperCase();
            }
            sidebarAvatarBtn.title = `${user.name} (${user.role})`;
            sidebarAvatarBtn.onclick = () => {
                if (user.professorId) {
                    this.navigate(`profesor/${user.professorId}`);
                } else {
                    this.navigate('profile');
                }
            };
        }
    },

    setupHeaderInfo() {
        this.setupAdminActions();
        const user = AuthManager.getCurrentUser();
        const profileMenu = document.getElementById('user-profile-menu');

        // SEGURIDAD: Ocultar barra de búsqueda superior completa para alumnos
        const topbarSearch = document.querySelector('.topbar-search');
        if (topbarSearch) {
            if (user && user.role === 'student') {
                topbarSearch.style.display = 'none';
            } else {
                topbarSearch.style.display = '';
            }
        }

        if (user && profileMenu) {
            let photoUrl = user.photo;

            // Try to find photo from professor profile if linked
            if (!photoUrl && user.professorId && typeof DataManager !== 'undefined') {
                const professor = DataManager.getProfessor(user.professorId);
                if (professor && professor.photo) {
                    photoUrl = professor.photo;
                }
            }

            const displayRole = user.role === 'admin' ? 'Administrador' : (user.role === 'professor' ? 'Docente' : (user.role === 'student' ? 'Alumno' : user.role));

            if (photoUrl) {
                StudentPhotos.setIdentityHTML(profileMenu, `
                    <div style="display: flex; align-items: center; gap: 0.75rem; border-left: 1px solid #cbd5e1; padding-left: 0.75rem; height: 38px;">
                        <div style="display: flex; flex-direction: column; text-align: right; justify-content: center;">
                            <span style="font-size: 0.88rem; font-weight: 700; color: #0f172a; line-height: 1.2;">${Utils.sanitizeHTML(user.name)}</span>
                            <span style="font-size: 0.75rem; color: #64748b; font-weight: 500; line-height: 1.1; margin-top: 1px;">${displayRole}</span>
                        </div>
                        <div style="flex-shrink: 0; display: flex; align-items: center; justify-content: center;">
                            <div style="padding: 0; overflow: hidden; width: 36px; height: 36px; border-radius: 50%; border: 2.5px solid #0284c7; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0,0,0,0.2); background: white; box-sizing: border-box;">
                                <img ${StudentPhotos.attributes(photoUrl)} style="width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 50%;" alt="${Utils.sanitizeHTML(user.name)}">
                            </div>
                        </div>
                    </div>
                `);
                profileMenu.title = `${user.name} (${user.role})`;
            } else {
                // Return to default if no photo
                StudentPhotos.setIdentityHTML(profileMenu, `
                    <div style="display: flex; align-items: center; gap: 0.75rem; border-left: 1px solid #cbd5e1; padding-left: 0.75rem; height: 38px;">
                        <div style="display: flex; flex-direction: column; text-align: right; justify-content: center;">
                            <span style="font-size: 0.88rem; font-weight: 700; color: #0f172a; line-height: 1.2;">${Utils.sanitizeHTML(user.name)}</span>
                            <span style="font-size: 0.75rem; color: #64748b; font-weight: 500; line-height: 1.1; margin-top: 1px;">${displayRole}</span>
                        </div>
                        <div class="user-avatar" id="header-user-avatar" title="${Utils.sanitizeHTML(user.name)} (${user.role})" style="width: 36px; height: 36px; border-radius: 50%; border: 2.5px solid #0284c7; background: var(--primary-gradient, linear-gradient(135deg, #14867A, #0E6B62)); color: white; font-weight: 700; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; box-shadow: 0 1px 3px rgba(0,0,0,0.2); flex-shrink: 0; box-sizing: border-box;">
                            ${user.name.charAt(0).toUpperCase()}
                        </div>
                    </div>
                `);
            }
        }

        // Setup Search Listener
        const searchInput = document.getElementById('global-search');
        const searchResults = document.getElementById('global-search-results');
        
        if (searchInput && searchResults) {
            // Close search on click outside
            document.addEventListener('click', (e) => {
                if (!searchInput.contains(e.target) && !searchResults.contains(e.target)) {
                    searchResults.style.display = 'none';
                }
            });

            searchInput.addEventListener('focus', () => {
                if (searchInput.value.trim().length > 1) {
                    searchResults.style.display = 'block';
                }
            });

            searchInput.addEventListener('input', (e) => {
                // SEGURIDAD: Bloquear búsqueda global para alumnos
                if (typeof AuthManager !== 'undefined' && AuthManager.isStudent()) {
                    searchResults.style.display = 'none';
                    SafeHTML.set(searchResults, '');
                    return;
                }

                const query = e.target.value.trim();
                if (query.length <= 1) {
                    searchResults.style.display = 'none';
                    SafeHTML.set(searchResults, '');
                    return;
                }

                const queryNormalized = typeof Utils !== 'undefined' && Utils.normalizeString ? Utils.normalizeString(query) : query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                let matches = [];

                // 1. Students
                const students = typeof DataManager !== 'undefined' ? (DataManager.getAll(DataManager.KEYS.STUDENTS) || []) : [];
                students.forEach(s => {
                    const searchStr = `${s.name || ''} ${s.lastName || ''} ${s.email || ''} ${s.phone || ''}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    if (searchStr.includes(queryNormalized)) {
                        matches.push({
                            title: `${s.name || ''} ${s.lastName || ''}`,
                            subtitle: s.email || 'Alumno',
                            icon: 'fa-user-graduate',
                            color: '#7c3aed',
                            bgColor: '#f5f3ff',
                            link: 'alumnado'
                        });
                    }
                });

                // 2. Professors
                const professors = typeof DataManager !== 'undefined' ? (DataManager.getProfessors() || []) : [];
                professors.forEach(p => {
                    const searchStr = `${p.firstName || ''} ${p.lastName || ''} ${p.email || ''} ${p.phone || ''} ${p.specialty || ''}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    if (searchStr.includes(queryNormalized)) {
                        matches.push({
                            title: `${p.firstName || ''} ${p.lastName || ''}`,
                            subtitle: p.specialty || 'Docente',
                            icon: 'fa-chalkboard-teacher',
                            color: '#2563eb',
                            bgColor: '#eff6ff',
                            link: `profesor/${p.id}`
                        });
                    }
                });

                // 3. Leads
                // Contacts are searched on the server (the browser no longer holds them all); when
                // they arrive the box is repainted if the text has not changed.
                const leads = typeof LeadService !== 'undefined'
                    ? (LeadService.searchCached(query, () => { if (searchInput.value.trim() === query) searchInput.dispatchEvent(new Event('input')); }) || [])
                    : [];
                leads.forEach(l => matches.push({
                    title: l.nombre || 'Lead',
                    subtitle: l.interes || 'Lead / CRM',
                    icon: 'fa-funnel-dollar',
                    color: '#d97706',
                    bgColor: '#fffbeb',
                    link: 'leads'
                }));

                // 4. Formations
                const formations = typeof DataManager !== 'undefined' ? (DataManager.getFormations() || []) : [];
                formations.forEach(f => {
                    const searchStr = `${f.name || ''} ${f.type || ''}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    if (searchStr.includes(queryNormalized)) {
                        matches.push({
                            title: f.name || 'Formación',
                            subtitle: `Curso · ${f.type || ''}`,
                            icon: 'fa-graduation-cap',
                            color: '#059669',
                            bgColor: '#f0fdf4',
                            link: 'formaciones'
                        });
                    }
                });

                const topMatches = matches.slice(0, 6);
                const escapedQuery = query.replace(/'/g, "\\'").replace(/"/g, '&quot;');

                if (topMatches.length === 0) {
                    SafeHTML.set(searchResults, `
                        <div style="padding: 1.25rem 1rem; text-align: center; color: #64748b; font-size: 0.85rem; font-weight: 500;">
                            No se encontraron resultados locales
                        </div>
                        <div ${UIEvents.attr("click", function(event,captured){if(typeof GlobalSearchView!=='undefined'){GlobalSearchView.searchQuery=String(captured[0]);}App.navigate('buscar');document.getElementById('global-search-results').style.display='none';document.getElementById('global-search').value='';}, [escapedQuery])} 
                             style="border-top: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; color: #0284c7; font-weight: 700; font-size: 0.825rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; background: #f0f9ff; border-bottom-left-radius: 12px; border-bottom-right-radius: 12px;"
                             ${UIEvents.attr("mouseover", function(event,captured){this.style.background='#e0f2fe';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='#f0f9ff';}, [])}>
                            <i class="fas fa-search-plus"></i> Ir al buscador avanzado
                        </div>
                    `);
                } else {
                    SafeHTML.set(searchResults, `
                        <div style="padding: 0.5rem; display: flex; flex-direction: column; gap: 2px; max-height: 300px; overflow-y: auto;">
                            ${topMatches.map(m => `
                                <div ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));document.getElementById('global-search-results').style.display='none';document.getElementById('global-search').value='';}, [m.link])} 
                                     style="display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 0.75rem; border-radius: 8px; cursor: pointer; transition: background 0.15s ease;"
                                     ${UIEvents.attr("mouseover", function(event,captured){this.style.background='#f1f5f9';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='transparent';}, [])}>
                                    <div style="width: 32px; height: 32px; border-radius: 6px; background: ${m.bgColor}; color: ${m.color}; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0;">
                                        <i class="fas ${m.icon}"></i>
                                    </div>
                                    <div style="min-width: 0; flex: 1;">
                                        <div style="font-weight: 600; font-size: 0.85rem; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${Utils.sanitizeHTML(m.title)}</div>
                                        <div style="font-size: 0.72rem; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500;">${Utils.sanitizeHTML(m.subtitle)}</div>
                                    </div>
                                    <i class="fas fa-chevron-right" style="font-size: 0.7rem; color: #94a3b8;"></i>
                                </div>
                            `).join('')}
                        </div>
                        <div ${UIEvents.attr("click", function(event,captured){if(typeof GlobalSearchView!=='undefined'){GlobalSearchView.searchQuery=String(captured[0]);}App.navigate('buscar');document.getElementById('global-search-results').style.display='none';document.getElementById('global-search').value='';}, [escapedQuery])} 
                             style="border-top: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; color: #0284c7; font-weight: 700; font-size: 0.825rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; background: #f0f9ff; border-bottom-left-radius: 12px; border-bottom-right-radius: 12px;"
                             ${UIEvents.attr("mouseover", function(event,captured){this.style.background='#e0f2fe';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='#f0f9ff';}, [])}>
                            <i class="fas fa-search-plus"></i> Ir al buscador avanzado
                        </div>
                    `);
                }
                searchResults.style.display = 'block';
            });
        }

        // Setup Notification and Messages Click Handlers
        const notifBtn = document.querySelector('.notification-btn');
        const messagesBtn = document.getElementById('header-messages-btn');

        if (notifBtn) {
            // Check for unread notifications
            const unreadNotifs = DataManager.getUnreadNotificationsCount(user.username);
            const badge = notifBtn.querySelector('.notification-badge');
            const icon = notifBtn.querySelector('i');

            const dropdownBadge = document.getElementById('dropdown-notifications-badge');
            if (dropdownBadge) {
                if (unreadNotifs > 0) {
                    dropdownBadge.style.display = 'inline-block';
                    dropdownBadge.textContent = unreadNotifs;
                } else {
                    dropdownBadge.style.display = 'none';
                }
            }

            if (unreadNotifs > 0) {
                if (badge) {
                    badge.style.display = 'block';
                    badge.style.width = '8px';
                    badge.style.height = '8px';
                    badge.style.background = '#ef4444'; // Rojo brillante explícito
                    badge.style.borderRadius = '50%';
                    badge.style.border = '2px solid white';
                }
                if (icon) {
                    icon.className = 'fas fa-bell'; // Solid icon
                }
                notifBtn.classList.add('notif-pulse');
            } else {
                if (badge) badge.style.display = 'none';
                if (icon) {
                    icon.className = 'far fa-bell'; // Outline icon
                }
                notifBtn.classList.remove('notif-pulse');
            }

            notifBtn.onclick = (e) => {
                this.toggleNotifications(e);
            };
        }

        if (messagesBtn) {
            const unreadCount = DataManager.getUnreadCount(user.username);
            const badge = document.getElementById('header-messages-badge');
            const icon = messagesBtn.querySelector('i');

            if (unreadCount > 0) {
                if (badge) {
                    // .has-count (ver header.css) es la variante "píldora con
                    // número" del mismo badge que usa la campanita como punto
                    // de 8px — sin esta clase, el número quedaba recortado
                    // dentro de un círculo demasiado pequeño para mostrarlo
                    // (bug real reportado: quería ver la cifra de correos
                    // nuevos, igual que la campanita mostraba las suyas).
                    badge.classList.add('has-count');
                    badge.style.display = 'flex';
                    badge.textContent = unreadCount > 99 ? '99+' : unreadCount;
                }
                if (icon) {
                    icon.className = 'fas fa-envelope';
                }
                messagesBtn.classList.add('notif-pulse');
            } else {
                if (badge) {
                    badge.style.display = 'none';
                    badge.classList.remove('has-count');
                    badge.textContent = '';
                }
                if (icon) {
                    icon.className = 'far fa-envelope';
                }
                messagesBtn.classList.remove('notif-pulse');
            }

            messagesBtn.onclick = () => {
                this.navigate('mensajes');
            };

            if (typeof DashboardView !== 'undefined' && DashboardView.updateMessagesWidget) {
                DashboardView.updateMessagesWidget();
            }
        }

        const profileBtn = document.getElementById('user-profile-menu');
        const profileDropdown = document.getElementById('profile-dropdown');
        if (profileBtn && profileDropdown) {
            profileBtn.onclick = (e) => {
                e.stopPropagation();
                
                // Toggle show class
                profileDropdown.classList.toggle('show');
                
                // Close when clicking outside
                if (profileDropdown.classList.contains('show')) {
                    const closeDropdown = (clickEvent) => {
                        if (!profileBtn.contains(clickEvent.target) && !profileDropdown.contains(clickEvent.target)) {
                            profileDropdown.classList.remove('show');
                            document.removeEventListener('click', closeDropdown);
                        }
                    };
                    document.addEventListener('click', closeDropdown);
                }
            };
            
            // Shell actions use data-sicaf-shell, not legacy inline onclick.
            // Close for any selected action, including clicks on its icon/label.
            // The theme switch remains open so its state is visible.
            profileDropdown.onclick = (e) => {
                e.stopPropagation();
                if (e.target.closest('.dropdown-item:not(.no-hover-effect)')) {
                    profileDropdown.classList.remove('show');
                }
            };

            // «Mi Perfil» (account profile) and «Configuración» are bound in core/shell-events.js.
            // Configuración is shown only to whoever may open it; others were silently sent back.
            const settingsItem = profileDropdown.querySelector('[data-sicaf-shell="9"]');
            if (settingsItem) settingsItem.style.display = (AuthManager.isAdmin() || AuthManager.hasModule('settings')) ? '' : 'none';

            // Configuración Email: bound by id.
            const emailSettingsLink = document.getElementById('dropdown-email-settings-btn');
            if (emailSettingsLink) {
                emailSettingsLink.onclick = (ev) => {
                    ev.preventDefault();
                    profileDropdown.classList.remove('show');
                    this.navigate('email-settings');
                };
            }

            // Set up Logout click
            const logoutLink = profileDropdown.querySelector('.logout-redesign-item');
            if (logoutLink) {
                logoutLink.removeAttribute('onclick');
                logoutLink.onclick = (ev) => {
                    ev.preventDefault();
                    profileDropdown.classList.remove('show');
                    Utils.showConfirm(
                        '¿Estás seguro de que quieres salir de la aplicación?',
                        () => AuthManager.logout()
                    );
                };
            }

            // Set up Notifications click
            const dropdownNotifBtn = document.getElementById('dropdown-notifications-btn');
            if (dropdownNotifBtn) {
                dropdownNotifBtn.onclick = (e) => {
                    profileDropdown.classList.remove('show');
                    this.toggleNotifications(e);
                };
            }

            // Update notifications badge in dropdown
            const unreadNotifs = DataManager.getUnreadNotificationsCount(user.username);
            const dropdownBadge = document.getElementById('dropdown-notifications-badge');
            if (dropdownBadge) {
                if (unreadNotifs > 0) {
                    dropdownBadge.style.display = 'inline-block';
                    dropdownBadge.textContent = unreadNotifs;
                } else {
                    dropdownBadge.style.display = 'none';
                }
            }

            // Dark Mode toggle
            const dmToggle = document.getElementById('dark-mode-toggle');
            if (dmToggle) {
                const isDarkMode = BrowserState.getItem('ecpv_dark_mode') === 'true';
                dmToggle.checked = isDarkMode;
                if (isDarkMode) {
                    document.body.classList.add('dark-theme');
                }
                dmToggle.onchange = () => {
                    if (dmToggle.checked) {
                        document.body.classList.add('dark-theme');
                        BrowserState.setItem('ecpv_dark_mode', 'true');
                        Utils.showToast('Modo oscuro activado', 'success');
                    } else {
                        document.body.classList.remove('dark-theme');
                        BrowserState.setItem('ecpv_dark_mode', 'false');
                        Utils.showToast('Modo oscuro desactivado', 'success');
                    }
                };
            }
        }
    },

    registerViews() {
        this.views = {
            'dashboard': DashboardView,
            'profesores': ProfessorsView,
            'profesores/altas': ProfessorsView,
            'profesores/activos': ProfessorsView,
            'profesores/curso': ProfessorsView,
            'profesores/nominas': NominasUpload,
            'nominas': ProfessorNominas,
            // Admin/RRHH's own payslips (their «Nóminas» manages everybody's).
            'mi-nomina': ProfessorNominas,
            'documentos': ProfessorDocs,
            'formaciones': Formaciones,
            'plan-estudios': { render: () => Formaciones.renderPlanEstudios() },
            'calendario': CalendarView,
            'calendario-formacion': ProgramView,
            'mensajes': MessagesView,
            'justificantes': JustificantesView,
            'planificacion-mensual': StaffPlanView,
            'settings': SettingsView,
            'email-settings': EmailSettingsView,
            'almacen': WarehouseView,
            'distribucion': DistributionView,
            'alumnado': (typeof StudentView !== 'undefined') ? StudentView : { render: () => '<div class="alert alert-danger">Error: La vista de alumnos no se ha podido cargar. Por favor, recarga la página.</div>' },
            'one-to-one': OneToOneView,
            'puertas-abiertas': OpenHouseView,
            'leads': (typeof LeadsView !== 'undefined') ? LeadsView : { render: () => '<div class="alert alert-danger">Error: La vista de contactos no se ha podido cargar. Por favor, recarga la página.</div>' },
            'buscar': GlobalSearchView,
            'email-templates': EmailTemplatesView,
            'acciones-pendientes': (typeof AccionesPendientesView !== 'undefined') ? AccionesPendientesView : { render: () => '<div style="padding:2rem;">Cargando...</div>' },
            'mapa-interactivo': (typeof InteractiveMapView !== 'undefined') ? InteractiveMapView : { render: () => '<div style="padding:2rem;">Cargando Diseñador del Mapa...</div>' },
            'profile': (typeof ProfileView !== 'undefined') ? ProfileView : { render: () => '<div style="padding:2rem;">Cargando perfil...</div>' },
            'academico': {
                render: () => CategoryDashboards.renderAcademico(),
                init: () => CategoryDashboards.initAcademico()
            },
            'alumnocrm': {
                render: () => CategoryDashboards.renderAlumnadoCRM(),
                init: () => CategoryDashboards.initAlumnadoCRM()
            },
            'rrhh': { render: () => CategoryDashboards.renderRRHH() },
            'logistica': { render: () => CategoryDashboards.renderLogistica() },
            'config': { render: () => CategoryDashboards.renderConfig() },
            'asistencia': (typeof AttendanceView !== 'undefined') ? AttendanceView : { render: () => '<div style="padding:2rem;">Cargando Control de Asistencia...</div>' },
            'alumno': (typeof StudentProfileView !== 'undefined') ? StudentProfileView : { render: () => '<div style="padding:2rem;">Cargando ficha de alumno...</div>' },
            'tareas': (typeof TasksStudentView !== 'undefined') ? TasksStudentView : { render: () => '<div style="padding:2rem;">Cargando mis tareas...</div>' },
            'gestion-tareas': (typeof TasksAdminView !== 'undefined') ? TasksAdminView : { render: () => '<div style="padding:2rem;">Cargando gestión de tareas...</div>' },
            'calificaciones': {
                render: () => AuthManager.isStudent() ? CalificacionesStudentView.render() : CalificacionesAdminView.render(),
                init: () => AuthManager.isStudent() ? CalificacionesStudentView.init() : CalificacionesAdminView.init(),
                destroy: () => { CalificacionesStudentView.destroy(); CalificacionesAdminView.destroy(); }
            },
            'calificaciones-admin': (typeof CalificacionesAdminView !== 'undefined') ? CalificacionesAdminView : { render: () => '<div style="padding:2rem;">Cargando Calificaciones...</div>' },
            'comunidad': (typeof ComunidadView !== 'undefined') ? ComunidadView : { render: () => '<div style="padding:2rem;">Cargando Comunidad...</div>' },
            'tutorias': (typeof TutoriasView !== 'undefined') ? TutoriasView : { render: () => '<div style="padding:2rem;">Cargando Tutorías...</div>' },
            'tutorias-admin': (typeof TutoriasAdminView !== 'undefined') ? TutoriasAdminView : { render: () => '<div style="padding:2rem;">Cargando Registro de Tutorías...</div>' },
            'memorias-asignatura': (typeof MemoriasAsignaturaView !== 'undefined') ? MemoriasAsignaturaView : { render: () => '<div style="padding:2rem;">Cargando Memorias de Asignatura...</div>' },
            'pre-matricula': (typeof PreMatriculaView !== 'undefined') ? PreMatriculaView : { render: () => '<div style="padding:2rem;">Cargando Pre-Matrícula...</div>' },
            'entrevistas': (typeof EntrevistasView !== 'undefined') ? EntrevistasView : { render: () => '<div style="padding:2rem;">Cargando Planificación de Entrevistas...</div>' },
            'permisos-roles': (typeof RolePermissionsView !== 'undefined') ? RolePermissionsView : { render: () => '<div style="padding:2rem;">Cargando Permisos por Rol...</div>' }
        };
        // setupSidebar with permissions
        this.setupSidebar();
    },

    setupSidebar() {
        const sidebar = document.getElementById('sidebar');

        // Apply Permission Filters to Sidebar Items
        const user = AuthManager.getCurrentUser();
        if (!user) return;

        // Dynamically adjust Tutorías link target based on role
        const tutoriasLink = document.querySelector('a[data-view="tutorias-admin"], a[data-view="tutorias"]');
        if (tutoriasLink) {
            if ((AuthManager.isProfessor() || AuthManager.isStudent()) && !AuthManager.hasModule('tutorias-admin')) {
                tutoriasLink.setAttribute('href', '#tutorias');
                tutoriasLink.setAttribute('data-view', 'tutorias');
            } else {
                tutoriasLink.setAttribute('href', '#tutorias-admin');
                tutoriasLink.setAttribute('data-view', 'tutorias-admin');
            }
        }

        this._applyNavPermissions();

        // Toggle Special Sections
        const laboralSection = document.getElementById('nav-section-laboral');
        const pinnedSection = document.getElementById('nav-section-pinned');

        // Dynamic Warehouse Link (Inject if not exists)
        let warehouseLink = document.querySelector('a[data-view="almacen"]');
        if (!warehouseLink) {
            // Find where to insert (after profesores or plan-estudios)
            const ref = document.querySelector('a[data-view="profesores"]');
            if (ref && ref.parentNode) {
                const link = document.createElement('a');
                link.href = '/almacen';
                link.className = 'nav-item';
                link.dataset.view = 'almacen';
                SafeHTML.set(link, '<i class="fas fa-boxes"></i><span class="nav-text">Almacén</span>');
                // Insert after
                ref.parentNode.insertBefore(link, ref.nextSibling);

                // Add click listener
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.navigate('almacen');
                });
            }
        }

        // Show/Hide Warehouse Link
        warehouseLink = document.querySelector('a[data-view="almacen"]');
        if (warehouseLink) {
            warehouseLink.style.display = (AuthManager.isAdmin() || AuthManager.isWarehouse() || AuthManager.isStudent() || AuthManager.hasModule('almacen')) ? 'flex' : 'none';
        }

        // Show/Hide Distribución Link
        const distLink = document.querySelector('a[data-view="distribucion"]');
        if (distLink) {
            if (AuthManager.isStudent()) {
                const studentId = AuthManager.getStudentId();
                distLink.style.display = 'none'; // Default hidden for students
                if (studentId) {
                    const token = AuthManager.getCsrfToken();
                    fetch(`/api/students/${studentId}/distribution`, {
                        headers: { 'X-SICAF-CSRF': token }
                    })
                    .then(res => res.json())
                    .then(data => {
                        if (data.success && data.distributionEnabled) {
                            distLink.style.display = 'flex';
                            if (distLink.classList.contains('sub-nav-item')) {
                                distLink.classList.remove('sub-nav-item');
                                const ref = document.querySelector('a[data-view="almacen"]');
                                if (ref && ref.parentNode) {
                                    ref.parentNode.insertBefore(distLink, ref.nextSibling);
                                }
                            }
                        }
                    })
                    .catch(err => console.error('Error fetching distribution status for sidebar:', err));
                }
            } else if (AuthManager.isProfessor() && !AuthManager.hasModule('distribucion')) {
                distLink.style.display = 'none'; // Hidden for professors
            } else {
                distLink.style.display = 'flex'; // Visible for admins/staff
            }
        }

        // 'Documentos' (documentación del personal, ProfessorDocs): cada profesor ve la suya;
        // admin, rrhh y el módulo 'documentos' ven la de todos. Sin ficha de profesor no hay nada que ver.
        const docsLink = document.querySelector('.sidebar .flyout-item[data-view="documentos"]');
        if (docsLink && docsLink.style.display !== 'none') {
            const canOpenDocs = AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('documentos') ||
                (!!user.professorId && !AuthManager.isStudent() && !AuthManager.isWarehouse());
            docsLink.style.display = canOpenDocs ? 'flex' : 'none';
            if (!canOpenDocs) docsLink.dataset.roleHidden = 'true';
        }

        if (laboralSection) {
            // Visible for Professors and RRHH
            laboralSection.style.display = (AuthManager.isProfessor() || AuthManager.isRRHH() || AuthManager.hasModule('nominas') || AuthManager.hasModule('justificantes')) ? 'block' : 'none';

            // For RRHH, hide "Nóminas" (they use the one in Professors view)
            const nominasItem = laboralSection.querySelector('[data-view="nominas"]');
            if (nominasItem) {
                nominasItem.style.display = AuthManager.isRRHH() ? 'none' : 'flex';
            }
        }
        if (pinnedSection) {
            pinnedSection.style.display = (AuthManager.isProfessor() || AuthManager.isStudent() || AuthManager.isWarehouse()) ? 'none' : 'block';
        }

        // Dynamic Students Link (Inject if not exists)
        // Only for Admin, RRHH
        if (AuthManager.isAdmin() || AuthManager.isRRHH()) {
            let studentsLink = document.querySelector('a[data-view="alumnado"]');
            if (!studentsLink) {
                // Insert after Professors
                const ref = document.querySelector('a[data-view="profesores"]');
                if (ref && ref.parentNode) {
                    const link = document.createElement('a');
                    link.href = '/alumnado';
                    link.className = 'nav-item';
                    link.dataset.view = 'alumnado';
                    SafeHTML.set(link, '<i class="fas fa-user-graduate"></i><span class="nav-text">Alumnado</span>');
                    ref.parentNode.insertBefore(link, ref.nextSibling);

                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.navigate('alumnado');
                    });
                }
            }
        }

        // Dynamic Leads Link (Inject if not exists)
        // Only for Admin, RRHH
        if (AuthManager.isAdmin() || AuthManager.isRRHH()) {
            let leadsLink = document.querySelector('a[data-view="leads"]');
            if (!leadsLink) {
                // Insert after Alumnado (or Professors if Alumnado is not there)
                const ref = document.querySelector('a[data-view="alumnado"]') || document.querySelector('a[data-view="profesores"]');
                if (ref && ref.parentNode) {
                    const link = document.createElement('a');
                    link.href = '/leads';
                    link.className = 'nav-item';
                    link.dataset.view = 'leads';
                    SafeHTML.set(link, '<i class="fas fa-address-book"></i><span class="nav-text">Contactos (Leads)</span>');
                    ref.parentNode.insertBefore(link, ref.nextSibling);

                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.navigate('leads');
                    });
                }
            }
        }

        // Hide empty groups dynamically
        document.querySelectorAll('.nav-group').forEach(group => {
            const items = group.querySelectorAll('.nav-group-items .nav-item');
            let visibleCount = 0;
            items.forEach(item => {
                if (item.style.display !== 'none') {
                    visibleCount++;
                }
            });
            if (visibleCount === 0) {
                group.style.display = 'none';
            } else {
                group.style.display = 'flex';
            }
        });

        // Configure Dashboard/Home link for student
        const dashboardLink = document.querySelector('a[data-view="dashboard"]');
        if (dashboardLink) {
            if (AuthManager.isStudent()) {
                const studentId = AuthManager.getStudentId();
                const ownStudent = (typeof StudentService !== 'undefined') ? StudentService.getStudent(studentId) : null;
                const ownProfileRoute = ownStudent ? Utils.studentProfileUrl(ownStudent) : `alumno/${studentId}`;
                dashboardLink.href = `/${ownProfileRoute}`;
                dashboardLink.dataset.view = ownProfileRoute;
                dashboardLink.style.display = 'flex'; // Ensure visible
                SafeHTML.set(dashboardLink, '<i class="fas fa-home"></i><span class="nav-text">Inicio</span>');
                
                // Add click listener
                dashboardLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.navigate(ownProfileRoute);
                });
            } else {
                dashboardLink.href = '/dashboard';
                dashboardLink.dataset.view = 'dashboard';
                SafeHTML.set(dashboardLink, '<i class="fas fa-home"></i><span class="nav-text">Inicio</span>');
            }
        }

        // Student-specific Sidebar Flattening (remove accordion structure)
        if (AuthManager.isStudent()) {
            document.querySelectorAll('.nav-group').forEach(group => {
                const items = group.querySelectorAll('.nav-group-items .nav-item');
                items.forEach(item => {
                    if (item.style.display !== 'none') {
                        item.classList.remove('sub-nav-item');
                        group.parentNode.insertBefore(item, group);
                    }
                });
                group.style.display = 'none';
            });
            
            // Hide separators or unnecessary dividers
            document.querySelectorAll('.nav-separator').forEach(sep => {
                sep.style.display = 'none';
            });
        }

        // Render Folder Tree for Professors
        if (AuthManager.isProfessor()) {
            this.renderSidebarFolderTree();
            this.setupSidebarFolderToggle();
        }
    },

    // Aplica los filtros de visibilidad por rol a los items de navegación
    // (sidebar principal + flyouts de nivel 2). Extraído a método propio
    // (antes vivía inline dentro de setupSidebar()) para poder volver a
    // llamarlo desde hideFlyout(): ese buscador interno del flyout resetea
    // `item.style.display` de TODOS sus items a su valor por defecto para
    // la próxima apertura, y sin re-aplicar esto aquí, cerrar y reabrir un
    // flyout hacía reaparecer enlaces que deberían seguir ocultos para el
    // rol actual (p.ej. "Mis Tareas" volvía a verse como admin).
    _applyNavPermissions() {
        const navItems = document.querySelectorAll('.sidebar-nav .nav-item, .sidebar-footer .nav-item, .sidebar .flyout-item');

        navItems.forEach(item => {
            const view = item.dataset.view;
            let visible = view ? RolePermissions.canView(view) : true;

            // El Asistente IA no es una "sección" de la matriz de permisos (es una
            // herramienta transversal): Administración, Coordinación y RRHH (el servidor también lo exige).
            if (item.id === 'ai-assistant-trigger') {
                visible = ['admin', 'coordinator', 'rrhh'].includes(AuthManager.getCurrentUser()?.role);
            }

            item.style.display = visible ? 'flex' : 'none';

            // Marca si el rol actual tiene vetado este item — el buscador del
            // flyout (más abajo) lo consulta para no volver a mostrarlo solo
            // porque el texto buscado coincide con su etiqueta.
            item.dataset.roleHidden = visible ? 'false' : 'true';
        });

        // Si a un grupo (Académico / Alumnado y CRM / RRHH / Logística) no le queda
        // ningún enlace visible en su desplegable, ocultar directamente el botón de
        // cabecera — un desplegable vacío no sirve de nada.
        const GROUP_FLYOUTS = {
            academico: 'flyout-academico',
            alumnocrm: 'flyout-alumnocrm',
            rrhh: 'flyout-rrhh',
            logistica: 'flyout-logistica'
        };

        Object.entries(GROUP_FLYOUTS).forEach(([view, flyoutId]) => {
            const groupItem = document.querySelector(`.sidebar-nav .nav-item[data-view="${view}"]`);
            const flyout = document.getElementById(flyoutId);
            if (!groupItem || !flyout || groupItem.style.display === 'none') return;

            const hasVisibleChild = Array.from(flyout.querySelectorAll('.flyout-item'))
                .some(child => child.style.display !== 'none');

            if (!hasVisibleChild) {
                groupItem.style.display = 'none';
                groupItem.dataset.roleHidden = 'true';
            }
        });
    },

    setupSidebarFolderToggle() {
        const docsNavItem = document.getElementById('docs-nav-item');
        const treeContainer = document.getElementById('sidebar-folder-tree');
        if (!docsNavItem || !treeContainer) return;

        docsNavItem.addEventListener('click', (e) => {
            // Prevent navigation if clicking the chevron area specifically
            // but here we want it to toggle and navigate
            const isChevron = e.target.closest('.toggle-icon');
            if (isChevron) {
                e.preventDefault();
                e.stopPropagation();
                const isHidden = treeContainer.style.display === 'none';
                treeContainer.style.display = isHidden ? 'block' : 'none';
                docsNavItem.querySelector('.toggle-icon').style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
            }
        });
    },

    renderSidebarFolderTree() {
        const treeContainer = document.getElementById('sidebar-folder-tree');
        if (!treeContainer) return;

        // Ensure current display state is preserved or defaulted
        const currentDisplay = treeContainer.style.display;
        if (currentDisplay === '') treeContainer.style.display = 'none';

        // Get docs from FileStorage
        const data = FileStorage.getItem('ecpv_professor_docs');
        const allDocs = data ? JSON.parse(data) : [];
        const currentUser = AuthManager.currentUser;
        if (!currentUser || !currentUser.professorId) return;

        const profDocs = allDocs.filter(d => d.professorId === currentUser.professorId && d.type === 'folder');

        const buildTree = (parentId = null, level = 0) => {
            const children = profDocs.filter(d => d.parentId === parentId);
            if (children.length === 0) return '';

            return `
                <div class="folder-group" style="display: flex; flex-direction: column;">
                    ${children.map(folder => `
                        <div class="folder-item-container">
                            <a href="#"
                               ${UIEvents.attr("click", function(event,captured){event.preventDefault();ProfessorDocs.navigateTo(String(captured[0]));}, [folder.id])}
                               class="nav-item sidebar-folder-link" 
                               style="padding: 0.4rem 0.5rem; padding-left: ${0.5 + (level * 1.2)}rem; font-size: 0.85rem; color: rgba(255,255,255,0.7); display: flex; align-items: center; gap: 0.5rem; text-decoration: none; transition: all 0.2s; border-radius: 6px;">
                                <i class="fas fa-folder" style="font-size: 0.8rem; color: #F59E0B; opacity: 0.8;"></i>
                                <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${Utils.sanitizeHTML(folder.name)}</span>
                            </a>
                            ${buildTree(folder.id, level + 1)}
                        </div>
                    `).join('')}
                </div>
            `;
        };

        const treeHtml = buildTree(null);
        SafeHTML.set(treeContainer, treeHtml || '<p style="font-size: 0.75rem; color: rgba(255,255,255,0.4); font-style: italic; margin-left: 0.5rem;">Sin carpetas</p>');

        // Add some basic styling for the folder links
        if (!document.getElementById('sidebar-tree-styles')) {
            const style = document.createElement('style');
            style.id = 'sidebar-tree-styles';
            SafeHTML.set(style, `
                .sidebar-folder-link:hover {
                    background: rgba(255,255,255,0.05);
                    color: white !important;
                }
                .sidebar-folder-link i {
                    transition: transform 0.2s;
                }
                .sidebar-folder-link:hover i {
                    transform: scale(1.1);
                }
            `);
            document.head.appendChild(style);
        }
    },

    setupNavigation() {
        const sidebar = document.getElementById('sidebar');
        const flyout = document.getElementById('sidebar-flyout');
        const sidebarItems = document.querySelectorAll('.sidebar-nav .nav-item');

        // Keep second-level navigation inside the sidebar, matching the approved
        // workspace mockup instead of presenting it as a detached drawer.
        sidebarItems.forEach(item => {
            const sectionId = item.dataset.section;
            const section = sectionId ? document.getElementById(`flyout-${sectionId}`) : null;
            if (section) {
                section.classList.add('sidebar-submenu');
                item.insertAdjacentElement('afterend', section);
            }
        });

        const flyoutItems = document.querySelectorAll('.sidebar .flyout-item');

        let hideTimeout = null;
        const expandableItems = Array.from(sidebarItems).filter(item =>
            item.dataset.section && document.getElementById(`flyout-${item.dataset.section}`)
        );

        expandableItems.forEach(item => {
            item.setAttribute('aria-haspopup', 'true');
            item.setAttribute('aria-expanded', 'false');
            item.setAttribute('aria-controls', `flyout-${item.dataset.section}`);
        });

        const isCollapsedDesktop = () => sidebar.classList.contains('collapsed') && window.innerWidth > 992;

        const showFlyout = (sectionId, triggerItem) => {
            if (hideTimeout) clearTimeout(hideTimeout);

            // Deactivate all sections first
            document.querySelectorAll('.sidebar .flyout-section').forEach(sec => {
                sec.classList.remove('active');
                sec.style.top = '';
            });

            const targetSection = document.getElementById(`flyout-${sectionId}`);
            if (targetSection) {
                targetSection.classList.add('active');
                sidebar.classList.add('submenu-open');
                if (isCollapsedDesktop() && triggerItem) {
                    const rect = triggerItem.getBoundingClientRect();
                    const maxTop = window.innerHeight - targetSection.offsetHeight - 12;
                    targetSection.style.top = `${Math.max(12, Math.min(rect.top, maxTop))}px`;
                }
                expandableItems.forEach(item => {
                    item.setAttribute('aria-expanded', String(item.dataset.section === sectionId));
                });
            } else {
                hideFlyout();
            }
        };

        const hideFlyout = () => {
            document.querySelectorAll('.sidebar .flyout-section').forEach(sec => {
                sec.classList.remove('active');
                sec.style.top = '';
                // Limpia el buscador y vuelve a mostrar todos los items para la próxima apertura
                const search = sec.querySelector('.flyout-search');
                if (search) search.value = '';
                sec.querySelectorAll('.flyout-item').forEach(itemEl => { itemEl.style.display = ''; });
            });
            sidebar.classList.remove('submenu-open');
            expandableItems.forEach(item => item.setAttribute('aria-expanded', 'false'));
            // El reset de arriba es solo para el buscador interno del flyout —
            // sin volver a aplicar los permisos por rol aquí, cerrar y reabrir
            // un flyout hacía reaparecer enlaces que deberían seguir ocultos
            // (p.ej. "Mis Tareas" volvía a verse como admin).
            this._applyNavPermissions();
        };

        const queueHideFlyout = () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            // 450ms de margen (antes 250ms): entre el tile del icono (52px) y el panel
            // hay un hueco de ~18px de fondo del riel sin listeners propios — con 250ms
            // un recorrido de ratón no perfectamente recto podía cerrar el panel antes
            // de llegar a él.
            hideTimeout = setTimeout(() => {
                hideFlyout();
            }, 450);
        };

        const cancelHideFlyout = () => {
            if (hideTimeout) clearTimeout(hideTimeout);
        };

        // Buscador del panel Nivel 2: filtra los enlaces de su propia sección al escribir
        document.querySelectorAll('.sidebar .flyout-search').forEach(input => {
            input.addEventListener('input', () => {
                const term = input.value.trim().toLowerCase();
                const section = input.closest('.flyout-section');
                if (!section) return;
                section.querySelectorAll('.flyout-item').forEach(itemEl => {
                    if (itemEl.dataset.roleHidden === 'true') return; // vetado para este rol, el buscador no lo reactiva
                    const matches = !term || itemEl.textContent.toLowerCase().includes(term);
                    itemEl.style.display = matches ? '' : 'none';
                });
            });
            // No dejar que el foco/click en el input cierre el flyout por el listener de mouseleave
            input.addEventListener('click', (e) => e.stopPropagation());
        });

        // Main Sidebar Items Events
        sidebarItems.forEach(item => {
            const section = item.dataset.section;
            
            item.addEventListener('keydown', (event) => {
                if (!document.getElementById(`flyout-${section}`)) return;
                if (event.key === 'ArrowRight' || event.key === 'Enter') {
                    event.preventDefault();
                    showFlyout(section, item);
                    document.querySelector(`#flyout-${section} .flyout-item`)?.focus();
                } else if (event.key === 'Escape') {
                    hideFlyout();
                }
            });

            // Click navigation/toggle
            item.addEventListener('click', (e) => {
                if (document.getElementById(`flyout-${section}`)) {
                    e.preventDefault();
                    const isOpen = item.getAttribute('aria-expanded') === 'true';
                    if (isOpen && window.matchMedia('(max-width: 992px)').matches) {
                        hideFlyout();
                    } else {
                        showFlyout(section, item);
                    }
                }
                const view = item.dataset.view;
                if (view) {
                    this.navigate(view);
                }
            });

            // Collapsed icon rail: hover over the icon reveals its submenu.
            if (document.getElementById(`flyout-${section}`)) {
                item.addEventListener('mouseenter', () => {
                    if (!isCollapsedDesktop()) return;
                    cancelHideFlyout();
                    showFlyout(section, item);
                });
                item.addEventListener('mouseleave', () => {
                    if (!isCollapsedDesktop()) return;
                    queueHideFlyout();
                });
            }
        });

        // Keep the popover open while the pointer moves into it.
        document.querySelectorAll('.sidebar .flyout-section').forEach(sec => {
            sec.addEventListener('mouseenter', () => {
                if (isCollapsedDesktop()) cancelHideFlyout();
            });
            sec.addEventListener('mouseleave', () => {
                if (isCollapsedDesktop()) queueHideFlyout();
            });
        });

        if (sidebar) {
            sidebar.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    const activeSection = sidebar.querySelector('.flyout-section.active');
                    const sectionId = activeSection?.id.replace('flyout-', '');
                    hideFlyout();
                    document.querySelector(`.sidebar-nav .nav-item[data-section="${sectionId}"]`)?.focus();
                }
            });
        }

        flyoutItems.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const view = item.dataset.view;
                if (view) {
                    this.navigate(view);
                }
                hideFlyout();
            });
        });

        // Back/forward: the address is the source of truth. navigate() itself uses pushState,
        // which fires no event, so a view is never rendered twice.
        const fromAddress = () => {
            const view = this.currentRoute();
            // Moving to or from the public enrollment page needs a fresh page.
            const isPublicRoute = view.startsWith('inscribete');
            const wasPublic = document.body.classList.contains('public-enrollment-mode');
            if (isPublicRoute || wasPublic) {
                window.location.reload();
                return;
            }
            this.navigate(view, false);
        };
        // A stray "#<screen>" (an old link) becomes the clean address of that screen. Browsers fire
        // popstate and then hashchange for it: whichever comes first converts it, the other one
        // finds no "#" left, so the screen opens once. Other anchors ("#ai-assistant") are ignored.
        const onAddressChange = () => {
            const route = this.routeFromHash(window.location.hash);
            if (route) history.replaceState(null, '', '/' + route);
            else if (window.location.hash) return;
            fromAddress();
        };
        window.addEventListener('popstate', onAddressChange);
        window.addEventListener('hashchange', () => { if (window.location.hash) onAddressChange(); });
        // Internal links open their screen without reloading the page. Ctrl/Cmd-click, middle
        // click and «open in a new tab» keep working, because the link is a real address.
        document.addEventListener('click', (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const anchor = event.target.closest?.('a[href]');
            if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
            const url = new URL(anchor.href, window.location.href);
            if (url.origin !== window.location.origin) return;
            const route = url.hash ? (url.pathname === window.location.pathname ? this.routeFromHash(url.hash) : null)
                : (this.isAppRoute(url.pathname.replace(/^\/+/, '')) ? url.pathname.replace(/^\/+/, '') : null);
            if (!route) return;
            event.preventDefault();
            // Menu items already navigate in their own handler; otherwise the link does it.
            setTimeout(() => { if (this.currentRoute() !== route) this.navigate(route); }, 0);
        });
    },

    toggleNavGroup(headerEl) {
        const group = headerEl.closest('.nav-group');
        if (group) {
            const isExpanded = group.classList.contains('expanded');
            
            // Optionally close other groups to keep things tidy
            document.querySelectorAll('.nav-group').forEach(g => {
                if (g !== group) {
                    g.classList.remove('expanded');
                }
            });

            if (isExpanded) {
                group.classList.remove('expanded');
            } else {
                group.classList.add('expanded');
            }
        }
    },

    // Screens live in the address path: /alumnado, /alumno/<nombre>, /profesor/<nombre>. These
    // first segments belong to the server and are never screens.
    RESERVED_PATHS: new Set(['api', 'assets', 'baja', 'data', 'dist', 'js', 'school-assets', 'uploads', 'vendor',
        'inscribete', 'restablecer-contrasena', 'index.html', 'styles.css']),

    isAppRoute(route) {
        return typeof route === 'string' && /^[a-z][a-z0-9-]*(?:\/[^?#]*)?$/.test(route) && !this.RESERVED_PATHS.has(route.split('/')[0]);
    },

    currentRoute() {
        const route = window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
        return this.isAppRoute(route) ? route : 'dashboard';
    },

    // "#alumnado" (old links) → "alumnado"; anchors like "#ai-assistant" or "#ecpv-curtain" are not screens.
    routeFromHash(hash) {
        const route = String(hash || '').replace(/^#/, '');
        return route && !['ai-assistant', 'ecpv-curtain'].includes(route) && this.isAppRoute(route) ? route : null;
    },

    // Puts a screen in the address without reloading. replace: a better name for the same screen
    // (an id becomes the person's name) takes the place of the entry instead of adding one.
    setRoute(route, { replace = false } = {}) {
        const path = '/' + route;
        if (window.location.pathname === path && !window.location.hash) return;
        history[replace ? 'replaceState' : 'pushState'](null, '', path);
    },

    navigate(viewName, updateHash = true) {
        console.log('Navigating to:', viewName);

        // Parse view name and parameters
        const parts = viewName.split('/');
        const baseView = parts[0];
        const params = parts.slice(1);

        // A delegated child module does not grant its department dashboard.
        const delegatedGroups = Object.fromEntries(Object.entries(RolePermissions.moduleGroups).map(([key,value])=>[key.replace('-dash',''),value]));
        const groupEntry = RolePermissions.matrix[RolePermissions.keyForRoute(baseView,AuthManager.currentUser?.role)];
        if (!AuthManager.isAdmin() && !AuthManager.hasModule(baseView) && groupEntry && !groupEntry[AuthManager.currentUser?.role]) {
            const child=delegatedGroups[baseView]?.find(id=>AuthManager.hasModule(id));
            if(child){this.navigate(child,updateHash);return;}
        }

        if (baseView === 'planificacion-mensual' && !RolePermissions.canView('planificacion-mensual')) {
            Utils.showToast('Acceso denegado a esta sección', 'warning');
            this.navigate('dashboard', true);
            return;
        }

        // General Role Protection for Admin-only views
        if (['email-settings', 'email-templates'].includes(baseView) && !AuthManager.isAdmin() && !AuthManager.hasModule(baseView)) {
            console.warn(`Access denied to ${baseView} for non-admin user.`);
            this.navigate('dashboard', true);
            return;
        }

        // Role-based Access Control for Professors
        if (AuthManager.isProfessor()) {
            const myProfId = AuthManager.getProfessorId();

            // Views that are explicitly forbidden for professors
            const restrictedViews = ['dashboard', 'profesores', 'formaciones', 'plan-estudios', 'calendario', 'settings', 'one-to-one', 'puertas-abiertas', 'leads', 'pre-matricula', 'entrevistas', 'tutorias-admin'];

            if (restrictedViews.includes(baseView) && !AuthManager.hasModule(baseView)) {
                if (myProfId) {
                    console.warn(`Access denied for professor to view: ${baseView}. Redirecting to own profile.`);
                    // Prevent infinite loop if already on this view
                    if (viewName !== `profesor/${myProfId}`) {
                        this.navigate(`profesor/${myProfId}`, true);
                    }
                } else {
                    console.error('Professor ID missing during access control check. Redirecting to login.');
                    AuthManager.logout();
                }
                return;
            }

            // Restrict individual professor profile access
            if (baseView === 'profesor' && params.length > 0) {
                let requestedProfId = params[0];
                if (typeof ProfessorService !== 'undefined') {
                    const prof = ProfessorService.getProfessorBySlug(params[0]) || ProfessorService.getProfessor(params[0]);
                    if (prof) {
                        requestedProfId = prof.id;
                    }
                }
                if (requestedProfId !== myProfId && !AuthManager.hasModule('profesores')) {
                    if (myProfId) {
                        console.warn(`Professor attempted to access profile ${params[0]} instead of own: ${myProfId}`);
                        Utils.showToast('Solo puedes acceder a tu propio perfil', 'warning');
                        this.navigate(`profesor/${myProfId}`, true);
                    }
                    return;
                }
            }
        }

        // Role-based Access Control for Students
        if (AuthManager.isStudent()) {
            const myStudentId = AuthManager.getStudentId();
            
            // 1. Bloqueo estricto de perfiles y listados de profesores
            if (baseView === 'profesor' || baseView === 'profesores') {
                console.warn(`Student attempted to access professor views. Redirecting to own student profile.`);
                Utils.showToast('Acceso denegado: no puedes acceder a perfiles de profesores', 'warning');
                if (myStudentId) {
                    this.navigate(`alumno/${myStudentId}`, true);
                } else {
                    this.navigate('profile', true);
                }
                return;
            }

            // 2. Bloqueo de perfiles de otros alumnos
            if (baseView === 'alumno') {
                const requestedParam = params[0] || '';
                const uuidMatch = requestedParam.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
                const requestedStudentId = uuidMatch ? uuidMatch[0] : requestedParam;

                if (requestedStudentId && myStudentId && requestedStudentId !== myStudentId) {
                    console.warn(`Student attempted to access profile ${requestedStudentId} instead of own: ${myStudentId}`);
                    Utils.showToast('Solo puedes acceder a tu propio perfil de alumno', 'warning');
                    const ownStudent = typeof StudentService !== 'undefined' ? StudentService.getStudent(myStudentId) : null;
                    const ownRoute = ownStudent ? Utils.studentProfileUrl(ownStudent) : `alumno/${myStudentId}`;
                    this.navigate(ownRoute, true);
                    return;
                }
            }

            // 3. Bloqueo del buscador global
            if (baseView === 'buscar') {
                console.warn(`Student attempted to access global search. Redirecting to own student profile.`);
                Utils.showToast('Acceso denegado al buscador global', 'warning');
                if (myStudentId) {
                    const ownStudent = typeof StudentService !== 'undefined' ? StudentService.getStudent(myStudentId) : null;
                    const ownRoute = ownStudent ? Utils.studentProfileUrl(ownStudent) : `alumno/${myStudentId}`;
                    this.navigate(ownRoute, true);
                } else {
                    this.navigate('profile', true);
                }
                return;
            }

            // Redirect students to their own profile page instead of general/category dashboards
            if (['dashboard', 'alumnocrm', 'rrhh', 'alumnado', 'config', 'academico'].includes(baseView)) {
                if (myStudentId) {
                    const ownStudent = typeof StudentService !== 'undefined' ? StudentService.getStudent(myStudentId) : null;
                    const ownRoute = ownStudent ? Utils.studentProfileUrl(ownStudent) : `alumno/${myStudentId}`;
                    const currentUuid = (params[0] || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
                    const currentId = currentUuid ? currentUuid[0] : params[0];

                    if (baseView !== 'alumno' || currentId !== myStudentId) {
                        // Allow direct sub-views that students have access to
                        if (['mapa-interactivo', 'calendario-formacion', 'mensajes', 'profile', 'almacen', 'tareas', 'calificaciones', 'comunidad', 'distribucion'].includes(viewName)) {
                            // allow
                        } else {
                            this.navigate(ownRoute, true);
                            return;
                        }
                    }
                }
            }
 
            // Expanded student restricted views
            const restrictedViews = [
                'profesores', 
                'profesor',
                'buscar',
                'formaciones', 
                'plan-estudios', 
                'nominas', 
                'mi-nomina',
                'documentos', 
                'leads', 'one-to-one', 'puertas-abiertas',
                'alumnado', 
                'email-templates',
                'asistencia',
                'settings',
                'calendario',
                'gestion-tareas',
                'tutorias-admin'
            ];
            // 'justificantes' ya no está restringida: los alumnos pueden subir y ver
            // sus propios justificantes médicos (JustificantesView filtra por su studentId).

            if (restrictedViews.includes(baseView)) {
                console.warn(`Access denied for student to view: ${baseView}. Redirecting to profile.`);
                Utils.showToast('Acceso denegado a esta sección', 'warning');
                if (myStudentId) {
                    this.navigate(`alumno/${myStudentId}`, true);
                } else {
                    this.navigate('profile', true);
                }
                return;
            }
        }

        // Role-based Access Control for Warehouse
        if (AuthManager.isWarehouse()) {
            const restrictedViews = ['profesores', 'formaciones', 'plan-estudios', 'calendario-formacion', 'nominas', 'mi-nomina', 'documentos', 'one-to-one', 'puertas-abiertas', 'leads'];

            if (restrictedViews.includes(baseView) && !AuthManager.hasModule(baseView)) {
                console.warn(`Access denied for warehouse to view: ${baseView}. Redirecting to dashboard.`);
                this.navigate('dashboard', true);
                return;
            }
        }

        // Update the address (initial)
        if (updateHash) {
            // We defer this if it's a professor view to allow slug replacement
            if (!viewName.startsWith('profesor/')) {
                this.setRoute(viewName);
            }
        }

        // Update active nav item inside sidebar-flyout and sidebar
        // 1. Remove active state from all items
        document.querySelectorAll('.sidebar-nav .nav-item, .sidebar .flyout-item').forEach(item => {
            item.classList.remove('active');
            item.removeAttribute('aria-current');
        });

        // 2. Find and active the current flyout item (if it's a sub-item)
        let activeSection = null;
        const flyoutItem = document.querySelector(`.sidebar .flyout-item[data-view="${baseView}"]`);
        if (flyoutItem) {
            flyoutItem.classList.add('active');
            flyoutItem.setAttribute('aria-current', 'page');
            const parentSection = flyoutItem.closest('.flyout-section');
            if (parentSection) {
                activeSection = parentSection.id.replace('flyout-', '');
            }
        } else {
            // Check if it's a top-level view (like dashboard or ai-assistant)
            const mainItem = document.querySelector(`.sidebar-nav .nav-item[data-view="${baseView}"]`);
            if (mainItem) {
                activeSection = mainItem.dataset.section;
                mainItem.setAttribute('aria-current', 'page');
            }
        }

        // 3. Activate the corresponding main sidebar icon (resalta el tile del riel,
        // pero ya NO fuerza la apertura del panel Nivel 2: con el riel estrecho
        // permanente esto disparaba el overlay en cada re-render de la página —
        // por ejemplo Formaciones.setFilter() llama a App.navigate('formaciones')
        // solo para refrescar la lista, y eso reabría el panel Académico encima
        // aunque el usuario ya lo hubiera cerrado. El panel solo debe abrirse por
        // interacción explícita (hover/click en el icono del riel).
        if (activeSection) {
            const mainNav = document.querySelector(`.sidebar-nav .nav-item[data-section="${activeSection}"]`);
            if (mainNav) {
                mainNav.classList.add('active');
                if (!flyoutItem) mainNav.setAttribute('aria-current', 'page');
            }
        }

        // Get the view
        let view = this.views[baseView];
        let content;

        // Special handling for sub-routes and specific views
        if (baseView === 'profesor' && params.length > 0) {
            let professorId = params[0];

            // Check if param is possibly a slug (not a UUID)
            // UUID regex: 8-4-4-4-12 hex digits
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(professorId);

            if (isUUID) {
                // It's an ID, try to convert to Slug for URL
                if (typeof ProfessorService !== 'undefined') {
                    const professor = ProfessorService.getProfessor(professorId);
                    if (professor) {
                        const slug = ProfessorService.getSlug(professor);
                        if (slug) {
                            // Update URL to use slug
                            if (updateHash) {
                                this.setRoute(`profesor/${slug}`);
                            }
                        } else if (updateHash) {
                            this.setRoute(viewName); // Fallback to ID
                        }
                    } else if (updateHash) {
                        this.setRoute(viewName);
                    }
                }
            } else {
                // It's likely a slug, resolve to ID
                if (updateHash) {
                    this.setRoute(viewName); // Keep the slug in URL
                }

                if (typeof ProfessorService !== 'undefined') {
                    const prof = ProfessorService.getProfessorBySlug(professorId);
                    if (prof) {
                        professorId = prof.id; // Resolve actual ID for rendering
                    } else {
                        console.error('Professor not found for slug:', professorId);
                        Utils.showToast('Profesor no encontrado', 'error');
                        this.navigate('profesores', true);
                        return;
                    }
                }
            }

            view = ProfessorProfile;
            content = ProfessorProfile.render(professorId);

        } else if (baseView === 'alumnado') {
            view = StudentView;
            if (params.length > 0 && typeof StudentView !== 'undefined') {
                if (['admissions', 'rejected', 'in_progress', 'formations', 'registered'].includes(params[0])) {
                    if (params[0] === 'rejected') {
                        StudentView.currentSection = 'in_progress';
                        StudentView.inProgressSubSection = 'rejected';
                    } else if (params[0] === 'in_progress') {
                        StudentView.currentSection = 'in_progress';
                        StudentView.inProgressSubSection = (params[1] === 'rejected') ? 'rejected' : 'drafts';
                    } else {
                        StudentView.currentSection = params[0];
                    }
                }
            }
            content = StudentView.render();
        } else if (baseView === 'alumno' && params.length > 0) {
            // URL amigable "alumno/nombre-apellido-<uuid>" (ver Utils.studentProfileUrl)
            // — el id real es el UUID al final, se extrae con una regex en vez de
            // asumir que todo el segmento es el id, para que los enlaces antiguos
            // "alumno/<uuid>" (sin nombre) también sigan funcionando igual.
            const uuidMatch = params[0].match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
            const studentId = uuidMatch ? uuidMatch[0] : params[0];

            // Si la URL es solo el UUID en bruto, actualizar la URL amigable con nombre
            if (updateHash && typeof StudentService !== 'undefined') {
                const sObj = StudentService.getStudent(studentId);
                if (sObj) {
                    const friendlyRoute = Utils.studentProfileUrl(sObj);
                    if (friendlyRoute && friendlyRoute !== `alumno/${params[0]}`) {
                        this.setRoute(friendlyRoute, { replace: true });
                    }
                }
            }

            view = StudentProfileView;
            content = StudentProfileView.render(studentId);
        } else if (baseView === 'nominas') {
            if (AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('nominas')) {
                view = NominasUpload;
                content = NominasUpload.render();
            } else {
                view = ProfessorNominas;
                content = ProfessorNominas.render();
            }
        } else if (baseView === 'profesores' && params.length > 0) {
            const subView = params[0];
            if (subView === 'nominas') {
                if (!AuthManager.isAdmin() && !AuthManager.isRRHH() && !AuthManager.hasModule('nominas')) {
                    Utils.showToast('Acceso restringido', 'error', true);
                    this.navigate('dashboard');
                    return;
                }
                view = NominasUpload;
                content = NominasUpload.render();
            } else if (subView === 'altas' || subView === 'activos' || subView === 'curso') {
                if (!AuthManager.isAdmin() && !AuthManager.isRRHH() && !AuthManager.hasModule('personal-laboral')) {
                    Utils.showToast('Acceso restringido', 'error', true);
                    this.navigate('dashboard');
                    return;
                }
                view = ProfessorsView;
                content = ProfessorsView.render(subView);
            } else {
                view = ProfessorsView;
                content = ProfessorsView.render();
            }
        } else if (view) {
            content = view.render();
        } else {
            // An unknown address (an old link, a typo) goes to the start page, never a blank one.
            console.warn('View not found:', baseView);
            if (baseView !== 'dashboard') return this.navigate('dashboard');
            return;
        }

        // Render the view
        const contentArea = document.getElementById('content-area');
        if (contentArea) {
            if (this.activeViewInstance && typeof this.activeViewInstance.destroy === 'function') {
                try { this.activeViewInstance.destroy(); } catch(e) {}
            }
            SafeHTML.set(contentArea, content);
            // Reset scroll to top of page on view navigation
            const resetScroll = () => {
                const mainContent = document.querySelector('.main-content');
                if (mainContent) mainContent.scrollTop = 0;
                if (contentArea) contentArea.scrollTop = 0;
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            };
            resetScroll();
            requestAnimationFrame(resetScroll);

            // Attach view-specific event listeners
            if (view && view.attachEventListeners) {
                view.attachEventListeners();
            }

            // Initialize view-specific functionality
            if (view && view.init) {
                view.init();
            }
            requestAnimationFrame(resetScroll);


        }

        // Update Breadcrumbs & Topbar Section Badge
        this.updateBreadcrumbs(viewName);
        this.updateTopbarSectionName(baseView, params);

        this.currentView = viewName;
        // Lets styles depend on the screen (e.g. no floating «+» over Comunidad's «Enviar»).
        document.body.dataset.view = String(viewName).split('/')[0];
    },

    updateTopbarSectionName(baseView, params = []) {
        const statusTextEl = document.getElementById('topbar-status-text');
        const statusContainer = document.querySelector('.topbar-status');
        if (!statusTextEl) return;

        const routeMap = {
            'dashboard': { text: 'Dashboard General', icon: 'fa-chart-pie' },
            'academico': { text: 'Dashboard Académico', icon: 'fa-graduation-cap' },
            'asistencia': { text: 'Control de Asistencia', icon: 'fa-calendar-check' },
            'calificaciones': { text: 'Calificaciones y Actas', icon: 'fa-file-signature' },
            'calendario': { text: 'Calendario General', icon: 'fa-calendar-alt' },
            'calendario-formacion': { text: 'Programación Lectiva', icon: 'fa-calendar-week' },
            'profesores': { text: 'Gestión de Profesores', icon: 'fa-user-tie' },
            'alumnado': { text: 'Gestión de Alumnado', icon: 'fa-user-graduate' },
            'leads': { text: 'Contactos y Leads', icon: 'fa-address-book' },
            'formaciones': { text: 'Formaciones y Cursos', icon: 'fa-layer-group' },
            'plan-estudios': { text: 'Plan de Estudios', icon: 'fa-book-open' },
            'almacen': { text: 'Almacén y Materiales', icon: 'fa-boxes-packing' },
            'distribucion': { text: 'Distribución y Catálogo', icon: 'fa-film' },
            'mensajes': { text: 'Mensajería Interna', icon: 'fa-comments' },
            'tutorias': { text: 'Tutorías Alumnado', icon: 'fa-headset' },
            'tutorias-admin': { text: 'Gestión de Tutorías', icon: 'fa-headset' },
            'planificacion-mensual': { text: 'Planificación mensual', icon: 'fa-calendar-days' },
            'justificantes': { text: 'Gestión de Justificantes', icon: 'fa-file-invoice' },
            'tareas': { text: 'Mis Tareas', icon: 'fa-tasks' },
            'gestion-tareas': { text: 'Gestión de Tareas', icon: 'fa-list-check' },
            'pre-matricula': { text: 'Pre-Matrícula', icon: 'fa-user-plus' },
            'entrevistas': { text: 'Entrevistas de Admisión', icon: 'fa-clipboard-user' },
            'comunidad': { text: SchoolService.label('Comunidad'), icon: 'fa-users' },
            'settings': { text: 'Configuración del Sistema', icon: 'fa-cog' },
            'email-settings': { text: 'Configuración Email', icon: 'fa-envelope' },
            'email-templates': { text: 'Plantillas Email', icon: 'fa-file-code' },
            'mapa-interactivo': { text: 'Mapa Interactivo', icon: 'fa-map' },
            'profile': { text: 'Mi Perfil', icon: 'fa-user-circle' },
            'profesor': { text: 'Ficha de Profesor', icon: 'fa-user-tie' },
            'alumno': { text: 'Ficha de Alumno', icon: 'fa-id-card' },
            'alumnocrm': { text: 'CRM Alumnado', icon: 'fa-users-cog' },
            'rrhh': { text: 'Recursos Humanos', icon: 'fa-id-card-clip' },
            'logistica': { text: 'Logística', icon: 'fa-truck' },
            'memorias-asignatura': { text: 'Memorias de Asignatura', icon: 'fa-file-lines' },
            'permisos-roles': { text: 'Permisos por Rol', icon: 'fa-user-shield' }
        };

        const routeInfo = routeMap[baseView] || { text: SchoolService.name(), icon: 'fa-film' };
        
        statusTextEl.textContent = routeInfo.text;
        
        if (statusContainer) {
            let iconEl = statusContainer.querySelector('i');
            if (iconEl) {
                iconEl.className = `fas ${routeInfo.icon}`;
            }
        }
    },

    updateBreadcrumbs(viewName) {
        const breadcrumbs = document.getElementById('breadcrumbs');
        if (!breadcrumbs) return;

        const parts = viewName.split('/');
        const baseView = parts[0];
        const params = parts.slice(1);

        // View Categories Mapping for Hierarchical Path
        const categories = {
            'formaciones': 'Académico',
            'plan-estudios': 'Académico',
            'calendario': 'Académico',
            'calendario-formacion': 'Académico',
            'alumnado': 'Alumnado y CRM',
            'one-to-one': 'Alumnado y CRM',
            'puertas-abiertas': 'Alumnado y CRM',
            'leads': 'Alumnado y CRM',
            'email-templates': 'Alumnado y CRM',
            'alumno': 'Alumnado y CRM',
            'pre-matricula': 'Alumnado y CRM',
            'entrevistas': 'Alumnado y CRM',
            'profesores': 'Personal y RRHH',
            'profesor': 'Personal y RRHH',
            'nominas': 'Personal y RRHH',
            'mi-nomina': 'Personal y RRHH',
            'justificantes': 'Personal y RRHH',
            'tutorias-admin': 'Personal y RRHH',
            'planificacion-mensual': 'Personal y RRHH',
            'almacen': 'Logística',
            'distribucion': 'Logística',
            'settings': 'Configuración',
            'email-settings': 'Configuración',
            'mapa-interactivo': 'Configuración',
            'profile': 'Configuración',
            'asistencia': 'Académico',
            'tareas': 'Académico',
            'gestion-tareas': 'Académico',
            'calificaciones': 'Académico',
            'comunidad': 'Académico',
            'memorias-asignatura': 'Académico'
        };

        // View Labels Mapping
        const isProf = AuthManager.isProfessor();
        const labels = {
            'dashboard': 'Escritorio',
            'mi-nomina': 'Mi nómina',
            'profesores': isProf ? 'Mi Perfil' : 'Profesores',
            'formaciones': 'Formaciones',
            'plan-estudios': 'Plan de Estudios',
            'calendario': 'Calendario Global',
            'calendario-formacion': 'Programación',
            'settings': 'Configuración',
            'email-settings': 'Ajustes de Correo',
            'profesor': isProf ? 'Mi Perfil' : 'Profesores',
            'program-view': 'Programación',
            'buscar': 'Buscador Global',
            'email-templates': 'Plantillas de Email CRM',
            'alumno': 'Alumnado',
            'alumnado': 'Alumnado',
            'one-to-one': 'One to One',
            'puertas-abiertas': 'Puertas abiertas',
            'leads': 'Contactos CRM',
            'almacen': 'Almacén',
            'distribucion': 'Distribución de Cortos',
            'mapa-interactivo': 'Mapa de Aulas',
            'profile': 'Mi Perfil',
            'academico': 'Área Académica',
            'alumnocrm': 'Alumnado y CRM',
            'rrhh': 'Personal y RRHH',
            'logistica': 'Logística',
            'config': 'Configuración',
            'asistencia': 'Pasar Lista',
            'tareas': 'Mis Tareas',
            'gestion-tareas': 'Gestión de Tareas',
            'calificaciones': 'Calificaciones',
            'comunidad': SchoolService.label('Comunidad'),
            'tutorias-admin': 'Control de Tutorías',
            'planificacion-mensual': 'Planificación mensual',
            'memorias-asignatura': 'Memorias de Asignatura',
            'pre-matricula': 'Pre-Matrícula e Ingresos',
            'entrevistas': 'Planificación de Entrevistas'
        };

        // Category View Route Mapping
        const categoryViews = {
            'Académico': 'academico',
            'Alumnado y CRM': 'alumnocrm',
            'Personal y RRHH': 'rrhh',
            'Logística': 'logistica',
            'Configuración': 'config'
        };

        const baseLabel = labels[baseView] || baseView.charAt(0).toUpperCase() + baseView.slice(1);
        const isActive = params.length === 0;

        let html = '';

        // Special Breadcrumb treatment when viewing own profile as student:
        // Instead of showing Admin navigation "Alumnado y CRM / Alumnado / <UUID>", show "Inicio / Mi Perfil / [Nombre Alumno]"
        if (AuthManager.isStudent()) {
            const myStudentId = AuthManager.getStudentId();
            const currentUuid = (params[0] || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
            const currentStudentId = currentUuid ? currentUuid[0] : params[0];

            if (baseView === 'alumno' && currentStudentId === myStudentId) {
                const student = (typeof StudentService !== 'undefined' ? StudentService.getStudent(myStudentId) : null) || AuthManager.getCurrentUser();
                const studentName = student ? (student.firstName ? `${student.firstName} ${student.lastName || ''}`.trim() : (student.name || '')) : '';

                SafeHTML.set(breadcrumbs, `
                    <div class="breadcrumb-item" ${UIEvents.attr("click", function(event,captured){App.navigate('dashboard');}, [])} style="cursor: pointer;">
                        <i class="fas fa-home"></i>
                    </div>
                    <div class="breadcrumb-item" ${UIEvents.attr("click", function(event,captured){App.navigate('dashboard');}, [])} style="cursor: pointer;">
                        Mi Perfil
                    </div>
                    <div class="breadcrumb-item active">
                        ${Utils.sanitizeHTML(studentName || 'Ficha de Alumno')}
                    </div>
                `);
                return;
            }
        }

        // Show Home Icon for all views except dashboard
        if (baseView !== 'dashboard') {
            html += `
                <div class="breadcrumb-item" ${UIEvents.attr("click", function(event,captured){App.navigate('dashboard');}, [])} style="cursor: pointer;">
                    <i class="fas fa-home"></i>
                </div>
            `;
        }

        // Parent Category Breadcrumb (hidden for students if it belongs to admin categories)
        const category = categories[baseView];
        if (category && (!AuthManager.isStudent() || !['Alumnado y CRM', 'Personal y RRHH'].includes(category))) {
            const catView = categoryViews[category];
            html += `
                <div class="breadcrumb-item" ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [catView])} style="cursor: pointer;">
                    ${Utils.sanitizeHTML(category)}
                </div>
            `;
        }

        // Base View
        html += `
            <div class="breadcrumb-item ${isActive ? 'active' : ''}" 
                 ${!isActive ? `${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [baseView === 'profesor' ? 'profesores' : baseView === 'alumno' ? (AuthManager.isStudent() ? 'dashboard' : 'alumnado') : baseView])} style="cursor: pointer;"` : ''}>
                ${Utils.sanitizeHTML(baseLabel)}
            </div>
        `;

        // Parameter (e.g. Professor Name or Student Name)
        if (params.length > 0) {
            let paramLabel = params[0];

            // Decode URL-encoded characters (e.g. %C3%A1 → á)
            try {
                paramLabel = decodeURIComponent(paramLabel).replace(/_/g, ' ');
            } catch (e) {
                paramLabel = paramLabel.replace(/_/g, ' ');
            }

            // If it's a professor, try to get the name
            if (baseView === 'profesor' && typeof DataManager !== 'undefined') {
                const prof = DataManager.getProfessor(params[0]);
                if (prof) {
                    paramLabel = prof.firstName ? `${prof.firstName} ${prof.lastName || ''}` : prof.name;
                }
            }

            // If it's a student, try to get the name
            if (baseView === 'alumno') {
                const uuidMatch = (params[0] || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
                const studentIdParam = uuidMatch ? uuidMatch[0] : params[0];
                let student = typeof StudentService !== 'undefined' ? StudentService.getStudent(studentIdParam) : null;
                if (!student && typeof AuthManager !== 'undefined' && AuthManager.getStudentId() === studentIdParam) {
                    student = AuthManager.getCurrentUser();
                }
                if (student) {
                    paramLabel = student.firstName ? `${student.firstName} ${student.lastName || ''}`.trim() : (student.name || paramLabel);
                }
            }

            html += `
                <div class="breadcrumb-item active">
                    ${Utils.sanitizeHTML(paramLabel)}
                </div>
            `;
        }

        SafeHTML.set(breadcrumbs, html);
    },

    setupModalHandlers() {
        const modalOverlay = document.getElementById('modal-overlay');
        const modalClose = document.getElementById('modal-close');

        if (modalClose) {
            modalClose.addEventListener('click', () => {
                if (modalOverlay) {
                    modalOverlay.classList.remove('active');
                }
            });
        }

        if (modalOverlay) {
            modalOverlay.addEventListener('click', (e) => {
                if (e.target === modalOverlay) {
                    modalOverlay.classList.remove('active');
                }
            });
        }
    },

    setupGlobalFormHandlers() {
        document.addEventListener('submit', (e) => {
            if (e.target.id === 'professor-form') {
                if (typeof ProfessorsView !== 'undefined' && ProfessorsView.saveProfessor) {
                    ProfessorsView.saveProfessor(e);
                }
            }
            if (e.target.id === 'professor-self-form') {
                if (typeof ProfessorProfile !== 'undefined' && ProfessorProfile.saveOwnProfile) {
                    ProfessorProfile.saveOwnProfile(e);
                }
            }
        });

        // Close notification dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const dropdown = document.getElementById('notification-dropdown');
            const notifBtn = document.querySelector('.notification-btn');
            if (dropdown && notifBtn && !dropdown.contains(e.target) && !notifBtn.contains(e.target)) {
                dropdown.remove();
            }
        });
    },

    toggleNotifications(e) {
        e.preventDefault();
        const existingDropdown = document.getElementById('notification-dropdown');
        if (existingDropdown) {
            existingDropdown.remove();
            return;
        }

        const user = AuthManager.getCurrentUser();
        const loaded = DataManager.getNotifications(user.username);
        const notifications = typeof FileStorage !== 'undefined' ? FileStorage.visibleCommunications('notifications', 'notifications', loaded) : loaded;
        const rect = e.currentTarget.getBoundingClientRect();

        const dropdown = document.createElement('div');
        dropdown.id = 'notification-dropdown';
        dropdown.className = 'notification-dropdown';
        dropdown.style.cssText = `
            position: absolute;
            top: ${rect.bottom + 10}px;
            right: 20px;
            width: 320px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            border: 1px solid var(--border-color);
            z-index: 1000;
            overflow: hidden;
            animation: fadeIn 0.2s ease-out;
        `;

        let content = `
            <div style="padding: 1rem; border-bottom: 1px solid var(--border-color); display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;">
                <h4 style="margin: 0; font-size: 1rem; font-weight: 600;">Notificaciones</h4>
                <div style="display: flex; gap: 0.5rem; align-items: center;">
                    ${notifications.length > 0 ? `
                        <button data-username="${Utils.sanitizeHTML(user.username)}" ${UIEvents.attr("click", function(event,captured){App.markAllNotificationsRead(this.dataset.username);}, [])} style="background: none; border: none; color: var(--primary-color); font-size: 0.75rem; font-weight: 700; cursor: pointer; padding: 0;">Marcar leídas</button>
                        <span style="color: var(--text-tertiary); font-size: 0.75rem;">|</span>
                        <button data-username="${Utils.sanitizeHTML(user.username)}" ${UIEvents.attr("click", function(event,captured){App.deleteAllNotifications(this.dataset.username);}, [])} style="background: none; border: none; color: #ef4444; font-size: 0.75rem; font-weight: 700; cursor: pointer; padding: 0;">Eliminar todas</button>
                    ` : ''}
                </div>
            </div>
            <div class="notification-list" style="max-height: 400px; overflow-y: auto;">
        `;

        if (notifications.length === 0) {
            content += `
                <div style="padding: 2rem; text-align: center; color: var(--text-secondary);">
                    <i class="far fa-bell" style="font-size: 2rem; margin-bottom: 0.5rem; opacity: 0.5;"></i>
                    <p style="margin: 0;">No tienes notificaciones</p>
                </div>
            `;
        } else {
            content += notifications.map(n => `
                <div class="notification-item ${n.read ? 'read' : 'unread'}" 
                     style="padding: 1rem; border-bottom: 1px solid var(--border-color); background: ${n.read ? 'white' : '#f0f9ff'}; cursor: pointer; transition: background 0.2s; position: relative;"
                     data-notification-id="${Utils.sanitizeHTML(n.id)}" ${UIEvents.attr("click", function(event,captured){App.readNotification(this.dataset.notificationId);}, [])}>
                    <div style="display: flex; gap: 0.75rem; padding-right: 2rem;">
                        <div style="color: var(--primary-color); margin-top: 2px;"><i class="fas fa-info-circle"></i></div>
                        <div style="flex: 1;">
                            <div style="font-weight: ${n.read ? '500' : '600'}; font-size: 0.9rem; margin-bottom: 0.25rem; color: var(--text-primary);">${Utils.sanitizeHTML(n.title)}</div>
                            <div style="font-size: 0.85rem; color: var(--text-secondary); line-height: 1.4;">${Utils.sanitizeHTML(n.message)}</div>
                            ${n.targetView ? '<div style="font-size: 0.75rem; color: var(--primary-color); margin-top: 0.25rem; font-weight: 500;"><i class="fas fa-external-link-alt" style="font-size: 0.65rem;"></i> Ver detalles</div>' : ''}
                            <div style="font-size: 0.75rem; color: var(--text-tertiary); margin-top: 0.5rem;">${new Date(n.date).toLocaleString()}</div>
                        </div>
                    </div>
                    <button data-notification-id="${Utils.sanitizeHTML(n.id)}" ${UIEvents.attr("click", function(event,captured){event.stopPropagation();App.deleteNotification(this.dataset.notificationId);}, [])}
                            style="position: absolute; top: 1rem; right: 0.75rem; background: none; border: none; color: #ef4444; opacity: 0.5; cursor: pointer; padding: 4px; transition: opacity 0.2s;"
                            ${UIEvents.attr("mouseover", function(event,captured){this.style.opacity='1';}, [])} 
                            ${UIEvents.attr("mouseout", function(event,captured){this.style.opacity='0.5';}, [])}
                            title="Eliminar aviso">
                        <i class="fas fa-trash-alt"></i>
                    </button>
                </div>
            `).join('');
        }

        if (typeof FileStorage !== 'undefined' && FileStorage.hasMoreCommunications('notifications')) {
            content += `
                <button type="button" ${UIEvents.attr("click", function(event,captured){App.loadOlderNotifications(this);}, [])}
                        style="display: block; width: 100%; padding: 0.75rem 1rem; background: none; border: none; color: var(--primary-color); font-size: 0.75rem; font-weight: 700; cursor: pointer;">
                    Ver anteriores
                </button>
            `;
        }

        content += '</div>';
        SafeHTML.set(dropdown, content);
        document.body.appendChild(dropdown);
    },

    async loadOlderNotifications(button) {
        if (button.disabled) return;
        button.disabled = true;
        const user = AuthManager.getCurrentUser();
        if (!await FileStorage.loadMoreCommunications('notifications', 'notifications')) {
            button.disabled = false;
            Utils.showToast('No se pudieron cargar más avisos. Revisa la conexión.', 'error');
            return;
        }
        if (user) this.redrawNotifications();
    },

    /** Redraws the open panel where the reader left it (older notices or a deleted one). */
    redrawNotifications() {
        const dropdown = document.getElementById('notification-dropdown');
        const notifBtn = document.querySelector('.notification-btn');
        if (!dropdown || !notifBtn) return;
        const scrollTop = dropdown.querySelector('.notification-list')?.scrollTop || 0;
        dropdown.remove();
        this.toggleNotifications({ preventDefault() {}, currentTarget: notifBtn });
        const list = document.querySelector('#notification-dropdown .notification-list');
        if (list) list.scrollTop = scrollTop;
    },

    readNotification(id) {
        const notifs = DataManager.getAll(DataManager.KEYS.NOTIFICATIONS);
        const notification = notifs.find(n => n.id === id);

        DataManager.markNotificationAsRead(id);
        const item = Array.from(document.querySelectorAll('.notification-item'))
            .find(node => node.dataset.notificationId === String(id));
        if (item) {
            item.style.background = 'white';
            const titleEl = item.querySelector('div[style*="font-weight"]');
            if (titleEl) titleEl.style.fontWeight = '500';
        }

        // Close dropdown
        const dropdown = document.getElementById('notification-dropdown');
        if (dropdown) dropdown.remove();

        // Navigate if targetView exists
        if (notification && notification.targetView) {
            let target = notification.targetView;
            if (notification.title === 'Entrevista Personal Solicitada') {
                target = 'entrevistas';
            } else if (notification.title?.includes('Superada') || notification.targetView === 'pre-matricula') {
                target = 'pre-matricula';
            }
            this.navigate(target);
        }

        this.setupHeaderInfo(); // Update badge
    },

    markAllNotificationsRead(userId) {
        const notifs = DataManager.getNotifications(userId);
        notifs.forEach(n => DataManager.markNotificationAsRead(n.id));
        document.getElementById('notification-dropdown').remove();
        this.setupHeaderInfo(); // Update badge
    },

    deleteAllNotifications(userId) {
        Utils.showConfirm('¿Estás seguro de que quieres eliminar todas las notificaciones?', async () => {
            // Also hides older notices that were never downloaded.
            if (!await FileStorage.hideAllNotifications()) {
                Utils.showToast('No se pudieron eliminar los avisos. Revisa la conexión.', 'error');
                return;
            }
            const dropdown = document.getElementById('notification-dropdown');
            if (dropdown) dropdown.remove();
            this.setupHeaderInfo(); // Update badge
        });
    },

    deleteNotification(id) {
        Utils.showConfirm('¿Estás seguro de que quieres eliminar esta notificación?', () => {
            DataManager.deleteNotification(id);

            // Fade the item out, then redraw the open list without it (empty state included).
            const item = Array.from(document.querySelectorAll('.notification-item'))
                .find(node => node.dataset.notificationId === String(id));
            if (item) {
                item.style.opacity = '0';
                item.style.transform = 'translateX(20px)';
            }
            setTimeout(() => this.redrawNotifications(), item ? 200 : 0);

            this.setupHeaderInfo(); // Update badge
        });
    },

    // Upcoming hires share the server-owned, once-per-hire-date reminder.
    checkUpcomingHires() {
        if (typeof ProfessorService === 'undefined' || typeof AuthManager === 'undefined') return;
        if (!['admin', 'rrhh'].includes(AuthManager.getCurrentUser()?.role)) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        for (const professor of DataManager.getProfessors() || []) {
            if (!professor.hireDate || professor.advisoryNotificationChecked) continue;
            const hire = new Date(professor.hireDate + 'T00:00:00');
            const days = Math.round((hire - today) / 86400000);
            if (days === 10) ProfessorService.checkDailyAdvisoryNotification(professor.id);
        }
    },

    render() {
        if (this.currentView) {
            this.navigate(this.currentView, false);
        }
    }
};

// Make App globally available BEFORE init
window.App = App;

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => App.init());
} else {
    App.init();
}
